import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormControl,
  Validators,
} from "@angular/forms";
import { DateAdapter } from "@angular/material/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
  EMPTY,
  forkJoin,
  interval,
  merge,
  Observable,
  of,
  Subject,
  timer,
} from "rxjs";
import {
  catchError,
  debounceTime,
  finalize,
  map,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import { ConfirmationDialogParams } from "../../shared/confirmation-dialog/confirmation-dialog.interface";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import { validatePhoneNumber } from "../../shared/validators/phone-number.validator";
import {
  type CardOrderUpsAccessPoint,
  FULFILLMENT_BLOCKED_REASON,
} from "../../card-orders/card-orders.interface";
import { CardOrdersUpsService } from "../../card-orders/card-orders-ups.service";
import {
  formatUpsAccessPointAddress,
  formatUpsAccessPointName,
  formatUpsAccessPointOpeningHours as localizeUpsAccessPointOpeningHours,
  formatShippingAddressLines,
  formatUpsPickupTime,
  getCountryDisplayName as getLocalizedCountryDisplayName,
  getCountryFlagUrl,
  createUpsPackageDescription,
  getBlockedPrinterOrderNumbers,
  getDeliverySpeedLabelKey,
  getProductionTimeLabelKey,
  getFulfillmentStageLabelKey,
  getShipmentCarrierLabelKey,
  getShipmentMethodLabelKey,
  getTemplateTypeLabelKey,
  getOrderTrackingNumber,
  getOrderUpsPackages,
  getOrderUpsShipmentDescription,
  getOrderUpsTracking,
  getOrderTotalQuantity,
  hasOrderExternalShipment,
  hasOrderUpsTracking,
  isOrderUpsDelivered,
  parseUpsPickupDate,
  printEmployeeNameLabel,
  upsCountryCode,
  upsNotificationLocale,
} from "../../card-orders/card-order.util";
import {
  AttachShipmentDialogData,
  PrintCardOrder,
  PrintCardFulfillmentTimelineStep,
  PrintCardNfcEncodingItem,
  PrintCardShipperDefaults,
  PrintCardUpsPackLineItem,
  PrintCardUpsPackPackagePlan,
  PrintCardUpsPackPlan,
  PrintCardUpsPackageDetails,
  PrintCardUpsPackageDraft,
  PrintCardUpsPickupDetails,
  PrintCardUpsTrackingDetails,
  SavePrintCardOrderUpsPackingPayload,
  ShipPrintCardOrderUpsPayload,
  SchedulePrintCardOrderUpsPickupPayload,
  UpsWeightUnit,
  UpdatePrintCardOrderShipmentPayload,
  UpsInternationalFormsFormValue,
  UpsNotificationChannel,
  UpsNotificationFormValue,
  UpsPickupScheduleFormValue,
  UpsRecipientFormValue,
  UpsShipperFormValue,
  UpsShipmentAccessorialsPayload,
  UpsShipmentFormValue,
  UpsShipmentNotificationPayload,
  UpsShipmentOptions,
  UpsShipmentSelectOption,
  UpsShippingLabelDialogItem,
} from "../print-card-orders.interface";
import { PrintCardOrdersService } from "../print-card-orders.service";
import { openPrintCardOrderItemPreviewDialog } from "../print-card-order-item-preview-dialog/print-card-order-item-preview.dialog";
import { AuthService } from "../../auth/auth.service";
import { UpsPackagePlannerComponent } from "../ups-package-planner/ups-package-planner.component";
import { UpsShippingLabelsDialogComponent } from "../../card-orders/ups-shipping-labels-dialog/ups-shipping-labels-dialog.component";
import { UpsShopRatesDialogComponent } from "../ups-shop-rates-dialog/ups-shop-rates-dialog.component";
import { AttachShipmentDialogComponent } from "../attach-shipment-dialog/attach-shipment-dialog.component";
import type { CardOrderStatus } from "../../card-orders/card-orders.interface";
import {
  isPrintCardOrderItemNfcEncodingComplete,
  isNfcPrintCardTemplateType,
  printCardOrderItemEncodedCount,
} from "../print-card-nfc.util";

const requiredValidator = (control: AbstractControl) =>
  Validators.required(control);

const emailValidator = (control: AbstractControl) => Validators.email(control);

const phoneValidators = [
  requiredValidator,
  (control: AbstractControl) =>
    validatePhoneNumber(control as UntypedFormControl),
];

const requiredFormTypesValidator = (control: AbstractControl) => {
  const value = control.value as string[] | null;
  return value?.length ? null : { required: true };
};

@Component({
  selector: "app-print-card-order-detail",
  templateUrl: "./print-card-order-detail.component.html",
  styleUrls: ["./print-card-order-detail.component.scss"],
  standalone: false,
})
export class PrintCardOrderDetailComponent implements OnInit, OnDestroy {
  @ViewChild(UpsPackagePlannerComponent)
  private upsPackagePlanner?: UpsPackagePlannerComponent;

  @ViewChild("accessPointPickerDialog")
  private accessPointPickerDialog?: TemplateRef<unknown>;

  readonly formatUpsAccessPointName = formatUpsAccessPointName;
  readonly formatUpsAccessPointAddress = formatUpsAccessPointAddress;
  readonly formatShippingAddressLines = formatShippingAddressLines;
  readonly getCountryFlagUrl = getCountryFlagUrl;
  readonly getFulfillmentStageLabelKey = getFulfillmentStageLabelKey;
  readonly getShipmentMethodLabelKey = getShipmentMethodLabelKey;
  readonly getDeliverySpeedLabelKey = getDeliverySpeedLabelKey;
  readonly getProductionTimeLabelKey = getProductionTimeLabelKey;
  readonly getShipmentCarrierLabelKey = getShipmentCarrierLabelKey;
  order: PrintCardOrder | null = null;
  upsOptions: UpsShipmentOptions | null = null;
  loading = true;
  shipping = false;
  savingParties = false;
  shipmentPartiesDirty = false;
  fulfillmentLoading = false;
  unmergeLoading = false;
  nfcEncodingLoading = false;
  nameLabelLoading: string | null = null;
  trackingDetails: PrintCardUpsTrackingDetails | null = null;
  upsPackLineItems: PrintCardUpsPackLineItem[] = [];
  upsPackages: PrintCardUpsPackageDraft[] = [];
  loadingPackPlan = false;
  upsPackingEditing = false;
  savingUpsPacking = false;
  accessPoints: CardOrderUpsAccessPoint[] = [];
  accessPointsLoading = false;
  readonly accessPointPickerControl = new FormControl<string>("", {
    nonNullable: true,
  });
  private accessPointsRequestSeq = 0;
  private accessPointPickerDialogRef: MatDialogRef<unknown> | null = null;
  readonly shipperForm: FormGroup;
  readonly recipientForm: FormGroup;
  readonly shipmentForm: FormGroup;
  readonly pickupScheduleForm: FormGroup;
  readonly internationalFormsForm: FormGroup;
  readonly notificationForm: FormGroup;
  shipmentAccessorialsForm: FormGroup | null = null;
  readonly errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  readonly partiesErrorStateMatcher =
    new ShowOnSubmitAttemptErrorStateMatcher();
  private partiesBaselineJson = "";
  private readonly destroy$ = new Subject<void>();
  private readonly stopPrinterSubmitPolling$ = new Subject<void>();
  private readonly printerSubmitPollIntervalMs = 2000;
  displayedPrinterSubmitUploadedCount = 0;
  displayedPrinterSubmitPercent = 0;
  pdfArchiveReadyCount = 0;
  pdfArchiveTotalCount = 0;
  private readonly printerSubmitCounterIntervals = new Map<
    "displayedPrinterSubmitUploadedCount" | "displayedPrinterSubmitPercent",
    ReturnType<typeof setInterval>
  >();
  private readonly dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly ordersService: PrintCardOrdersService,
    private readonly toastr: ToastrTranslatedService,
    private readonly dialog: MatDialog,
    private readonly fb: FormBuilder,
    private readonly upsService: CardOrdersUpsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
  ) {
    this.internationalFormsForm = this.createInternationalFormsForm();
    this.notificationForm = this.createNotificationForm();
    this.shipperForm = this.fb.group({
      name: ["", requiredValidator],
      line1: ["", requiredValidator],
      line2: [""],
      city: ["", requiredValidator],
      postalCode: ["", requiredValidator],
      country: ["PL", requiredValidator],
      phone: ["", phoneValidators],
    });

    this.recipientForm = this.fb.group({
      recipientName: ["", requiredValidator],
      companyName: [""],
      phone: ["", phoneValidators],
      line1: ["", requiredValidator],
      line2: [""],
      city: ["", requiredValidator],
      postalCode: ["", requiredValidator],
      country: ["PL", requiredValidator],
      addressLocationType: ["COMMERCIAL" as const, requiredValidator],
      deliveryType: ["COURIER" as const, requiredValidator],
      accessPointId: [""],
      accessPointName: [""],
      accessPointLine1: [""],
      accessPointCity: [""],
      accessPointPostalCode: [""],
      accessPointCountry: [""],
      accessPointOpeningHours: [""],
    });

    this.shipmentForm = this.fb.group({
      serviceCode: ["", requiredValidator],
      packagingCode: ["", requiredValidator],
      shipmentDescription: ["", requiredValidator],
      ratingRequestOption: ["Ratetimeintransit", requiredValidator],
      weightUnit: ["KGS", requiredValidator],
      dimensionUnit: ["CM", requiredValidator],
      deliveryConfirmation: [""],
      shipmentIndicationCode: [""],
      pickupEnabled: [true],
      pickupDate: [this.defaultPickupDate()],
      readyTime: [this.defaultPickupTime(9, 0)],
      closeTime: [this.defaultPickupTime(17, 0)],
      pickupPoint: [""],
      specialInstruction: [""],
    });

    this.pickupScheduleForm = this.fb.group({
      pickupDate: [this.defaultPickupDate(), requiredValidator],
      readyTime: [this.defaultPickupTime(9, 0), requiredValidator],
      closeTime: [this.defaultPickupTime(17, 0), requiredValidator],
      pickupPoint: [""],
      specialInstruction: [""],
    });
    this.syncShipmentPickupValidators(
      Boolean(this.shipmentForm.get("pickupEnabled")?.value),
    );
  }

  ngOnInit(): void {
    this.setupConditionalValidators();
    this.setupNotificationLocaleSync();
    this.setupPartiesDirtyTracking();
    this.setupRecipientDeliveryFlow();
    this.setupShipmentQuoteRefresh();

    this.route.paramMap
      .pipe(
        switchMap((params) => {
          const orderId = params.get("printCardOrderId")?.trim();
          if (!orderId) {
            this.loading = false;
            this.toastr.error("printCardOrders.loadFailed");
            void this.router.navigate(["/admin/print-card-orders"]);
            return EMPTY;
          }
          this.loading = true;
          return forkJoin({
            order: this.ordersService.getOrder(orderId),
            shipperDefaults: this.ordersService.getShipperDefaults(orderId),
            upsOptions: this.ordersService.getUpsShipmentOptions(),
          }).pipe(
            switchMap(
              ({
                order,
                shipperDefaults,
                upsOptions,
              }): Observable<{
                order: PrintCardOrder;
                shipperDefaults: PrintCardShipperDefaults;
                upsOptions: UpsShipmentOptions;
                packPlan: PrintCardUpsPackPlan | null;
              }> => {
                const packOrderId = order.printCardOrderId?.trim();
                const shouldLoadPackPlan =
                  !!packOrderId &&
                  this.isUpsPackingSectionAvailable(order) &&
                  !hasOrderUpsTracking(order);
                if (!shouldLoadPackPlan) {
                  return of({
                    order,
                    shipperDefaults,
                    upsOptions,
                    packPlan: null,
                  });
                }
                return this.ordersService.getUpsPackPlan(packOrderId).pipe(
                  map((packPlan) => ({
                    order,
                    shipperDefaults,
                    upsOptions,
                    packPlan,
                  })),
                  catchError(() =>
                    of({
                      order,
                      shipperDefaults,
                      upsOptions,
                      packPlan: null,
                    }),
                  ),
                );
              },
            ),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ order, shipperDefaults, upsOptions, packPlan }) => {
          try {
            this.upsOptions = upsOptions;
            this.shipmentAccessorialsForm = this.createAccessorialsForm(
              upsOptions.shipmentAccessorials,
            );
            this.applyShipperFromOrderOrDefaults(order, shipperDefaults);
            this.populateRecipientForm(order);
            this.capturePartiesBaseline();
            this.applyShipmentDefaults(order, upsOptions);
            this.initializeConditionalValidators();
            this.syncIncompatibleAccessorials();
            this.upsPackingEditing = false;
            this.applyOrderUpdate(order, { loadUpsPacking: false });
            if (packPlan) {
              this.upsPackLineItems = packPlan.items;
              if (this.orderHasSavedUpsPacking(order)) {
                this.upsPackages =
                  getOrderUpsPackages<PrintCardUpsPackageDetails>(order).map(
                    (pkg) => this.toUpsPackageDraft(pkg),
                  );
                this.sanitizeUpsPackagesFromBlockedItems(order);
                const packing = order.ups?.packing;
                if (packing) {
                  const currentShipment =
                    this.shipmentForm.getRawValue() as UpsShipmentFormValue;
                  const packingDcis = String(
                    packing.deliveryConfirmation ??
                      currentShipment.deliveryConfirmation ??
                      "",
                  ).trim();
                  this.shipmentForm.patchValue(
                    {
                      serviceCode:
                        packing.serviceCode ?? currentShipment.serviceCode,
                      packagingCode:
                        packing.packagingCode ?? currentShipment.packagingCode,
                      shipmentDescription:
                        packing.shipmentDescription ??
                        currentShipment.shipmentDescription,
                      weightUnit:
                        packing.weightUnit ?? currentShipment.weightUnit,
                      dimensionUnit:
                        packing.dimensionUnit ?? currentShipment.dimensionUnit,
                      deliveryConfirmation:
                        packingDcis === "2" || packingDcis === "3"
                          ? packingDcis
                          : "",
                      shipmentIndicationCode:
                        packing.shipmentIndicationCode ??
                        currentShipment.shipmentIndicationCode,
                    },
                    { emitEvent: false },
                  );
                }
              } else {
                this.upsPackages = packPlan.packages.map((pkg) =>
                  this.toUpsPackageDraft(pkg),
                );
              }
              this.cdr.markForCheck();
            }
          } catch {
            this.toastr.error("printCardOrders.loadFailed");
            void this.router.navigate(["/admin/print-card-orders"]);
          } finally {
            this.loading = false;
          }
        },
        error: () => {
          this.loading = false;
          this.toastr.error("printCardOrders.loadFailed");
          void this.router.navigate(["/admin/print-card-orders"]);
        },
      });

    interval(30000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          const orderId = this.order?.printCardOrderId?.trim();
          if (
            !orderId ||
            this.order?.status !== "PICKED_FROM_PRINTER" ||
            this.pendingNfcEncodingCount === 0
          ) {
            return EMPTY;
          }
          return this.ordersService.getOrder(orderId);
        }),
      )
      .subscribe({
        next: (order) => {
          if (!order) {
            return;
          }
          this.applyOrderUpdate(order, { loadUpsPacking: false });
        },
      });
  }

  ngOnDestroy(): void {
    for (const intervalId of this.printerSubmitCounterIntervals.values()) {
      clearInterval(intervalId);
    }
    this.printerSubmitCounterIntervals.clear();
    this.stopPrinterSubmitPolling$.next();
    this.stopPrinterSubmitPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isAccessPointDelivery(): boolean {
    return this.recipientForm.get("deliveryType")?.value === "ACCESS_POINT";
  }

  get isInternationalUpsShipment(): boolean {
    const shipFrom = upsCountryCode(
      String(this.shipperForm.get("country")?.value ?? "PL"),
    );
    const shipTo = upsCountryCode(
      String(this.recipientForm.get("country")?.value ?? "PL"),
    );
    return shipFrom !== shipTo;
  }

  get availableUpsServices(): UpsShipmentSelectOption[] {
    if (!this.upsOptions) {
      return [];
    }
    const allowedCodes = new Set(
      this.isInternationalUpsShipment
        ? (this.upsOptions.internationalServiceCodes ?? [])
        : (this.upsOptions.domesticServiceCodes ??
            this.upsOptions.services.map((option) => option.code)),
    );
    return this.upsOptions.services.filter((option) =>
      allowedCodes.has(option.code),
    );
  }

  isAccessorialOptionDisabled(code: string): boolean {
    if (code === "deliverToAddresseeOnly") {
      return (
        !this.isAccessPointDelivery ||
        this.shipmentForm.get("shipmentIndicationCode")?.value !== "01"
      );
    }
    if (code === "directDeliveryOnly") {
      return this.isAccessPointDelivery;
    }
    if (code === "dropoffAtUpsFacility") {
      if (this.isAccessPointDelivery) {
        return true;
      }
      return !this.isFreightUpsServiceSelected();
    }
    return false;
  }

  accessorialDisabledReasonKey(code: string): string | null {
    if (!this.isAccessorialOptionDisabled(code)) {
      return null;
    }
    if (code === "deliverToAddresseeOnly") {
      return "printCardOrders.shipment.hint.accessorial.deliverToAddresseeOnlyRequires01";
    }
    if (code === "directDeliveryOnly") {
      return "printCardOrders.shipment.hint.accessorial.directDeliveryOnlyIncompatibleWithAccessPoint";
    }
    if (code === "dropoffAtUpsFacility") {
      if (this.isAccessPointDelivery) {
        return "printCardOrders.shipment.hint.accessorial.dropoffAtUpsFacilityIncompatibleWithAccessPoint";
      }
      return "printCardOrders.shipment.hint.accessorial.dropoffAtUpsFacilityRequiresFreight";
    }
    return null;
  }

  private isFreightUpsServiceSelected(): boolean {
    const serviceCode = String(
      this.shipmentForm.get("serviceCode")?.value ?? "",
    ).trim();
    if (!serviceCode || !this.upsOptions) {
      return false;
    }
    return this.upsOptions.freightServiceCodes.includes(serviceCode);
  }

  get hasSelectedAccessPoint(): boolean {
    const { accessPointId } =
      this.recipientForm.getRawValue() as UpsRecipientFormValue;
    return Boolean(accessPointId.trim());
  }

  get selectedAccessPointView(): {
    name: string;
    address: string;
    openingHours: string | null;
  } | null {
    if (!this.hasSelectedAccessPoint) {
      return null;
    }
    const recipient = this.recipientForm.getRawValue() as UpsRecipientFormValue;
    return {
      name: formatUpsAccessPointName(recipient.accessPointName),
      address: formatUpsAccessPointAddress({
        line1: recipient.accessPointLine1,
        city: recipient.accessPointCity,
        postalCode: recipient.accessPointPostalCode,
      }),
      openingHours: this.formatUpsAccessPointOpeningHours(
        recipient.accessPointOpeningHours,
      ),
    };
  }

  getCountryDisplayName(alpha2Code: string | null | undefined): string {
    return getLocalizedCountryDisplayName(
      alpha2Code,
      this.translate.currentLang,
    );
  }

  formatUpsAccessPointOpeningHours(
    value: string | null | undefined,
  ): string | null {
    return localizeUpsAccessPointOpeningHours(
      value,
      (key) => this.translate.instant(key) as string,
    );
  }

  get hasUpsTracking(): boolean {
    return hasOrderUpsTracking(this.order);
  }

  get hasExternalShipment(): boolean {
    return hasOrderExternalShipment(this.order);
  }

  get canAttachManualShipment(): boolean {
    if (!this.order || this.hasUpsTracking || !this.upsOptions) {
      return false;
    }
    if (
      this.order.status === "CANCELLED" ||
      this.order.status === "DELIVERED"
    ) {
      return false;
    }
    const deliveryType = String(
      this.recipientForm.get("deliveryType")?.value ??
        this.order.deliveryType ??
        "",
    );
    return deliveryType === "COURIER" || deliveryType === "ACCESS_POINT";
  }

  get upsPickup(): PrintCardUpsPickupDetails | null {
    return this.order?.ups?.pickup ?? null;
  }

  readonly parseUpsPickupDate = parseUpsPickupDate;
  readonly formatUpsPickupTime = formatUpsPickupTime;
  readonly getTemplateTypeLabelKey = getTemplateTypeLabelKey;

  get hasActiveUpsPickup(): boolean {
    const pickup = this.upsPickup;
    return Boolean(pickup?.prn?.trim() && pickup.status !== "CANCELLED");
  }

  get canManageUpsPickup(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canManageUpsPickup);
  }

  get canScheduleUpsPickup(): boolean {
    return this.canManageUpsPickup && !this.hasActiveUpsPickup;
  }

  get canCancelUpsPickup(): boolean {
    return this.canManageUpsPickup && this.hasActiveUpsPickup;
  }

  get showUpsPickupSection(): boolean {
    return this.hasActiveUpsPickup || this.canScheduleUpsPickup;
  }

  get canShipWithUps(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canCreateUpsShipment);
  }

  get canVoidUpsShipment(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canVoidUpsShipment);
  }

  get canConfigureUpsShipment(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canConfigureUpsShipment);
  }

  get canQuoteUpsShipment(): boolean {
    return this.canConfigureUpsShipment || this.canShipWithUps;
  }

  get canEstimateUpsShippingCost(): boolean {
    if (!this.order || this.hasUpsTracking || !this.upsOptions) {
      return false;
    }
    if (
      this.order.status === "CANCELLED" ||
      this.order.status === "DELIVERED"
    ) {
      return false;
    }
    const deliveryType = String(
      this.recipientForm.get("deliveryType")?.value ??
        this.order.deliveryType ??
        "",
    );
    return deliveryType === "COURIER" || deliveryType === "ACCESS_POINT";
  }

  get showUpsPackingSection(): boolean {
    return this.isUpsPackingSectionAvailable();
  }

  private isUpsPackingSectionAvailable(
    order: PrintCardOrder | null = this.order,
  ): boolean {
    if (!order) {
      return false;
    }
    if (order.fulfillment?.actions.canConfigureUpsShipment) {
      return true;
    }
    const upsDelivery =
      order.deliveryType === "COURIER" || order.deliveryType === "ACCESS_POINT";
    return (
      upsDelivery &&
      (order.status === "PICKED_FROM_PRINTER" || order.status === "PACKED") &&
      !hasOrderUpsTracking(order)
    );
  }

  private applyOrderUpdate(
    order: PrintCardOrder,
    options: {
      loadUpsPacking?: boolean;
      resetPackingEdit?: boolean;
      syncPrinterSubmitPolling?: boolean;
    } = {},
  ): void {
    this.order = {
      ...order,
      printCardOrderId: order.printCardOrderId?.trim() ?? "",
    };
    this.trackingDetails = getOrderUpsTracking(this.order);
    this.syncOrderPdfArchiveCountersFromOrder();
    this.syncDisplayedPrinterSubmitCounters();
    if (options.resetPackingEdit !== false) {
      this.upsPackingEditing = false;
    }
    if (options.syncPrinterSubmitPolling !== false) {
      this.syncPrinterSubmitPolling();
    }
    this.syncUpsFormsLockState();
    this.cdr.detectChanges();
    if (!options.loadUpsPacking) {
      return;
    }
    this.loadUpsPackingFromOrder(this.order, true);
  }

  get canEditUpsPacking(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canEditUpsPacking);
  }

  get upsPackingLocked(): boolean {
    return this.canEditUpsPacking && !this.upsPackingEditing;
  }

  get atUpsPackingStage(): boolean {
    return this.order?.status === "PICKED_FROM_PRINTER" && !this.hasUpsTracking;
  }

  get upsPlanningActive(): boolean {
    return this.atUpsPackingStage || this.upsPackingEditing;
  }

  get upsPlannerDisabled(): boolean {
    return this.hasUpsTracking || !this.upsPlanningActive;
  }

  get upsFormsLocked(): boolean {
    return this.hasUpsTracking;
  }

  get orderUpsPackages(): PrintCardUpsPackageDetails[] {
    return getOrderUpsPackages<PrintCardUpsPackageDetails>(this.order);
  }

  get orderUpsShipmentDescription(): string | null {
    return getOrderUpsShipmentDescription(this.order);
  }

  get upsTrackingNumber(): string | null {
    return this.order ? getOrderTrackingNumber(this.order) : null;
  }

  get upsTrackingStatus(): string | null {
    return (
      this.trackingDetails?.status?.trim() ||
      this.order?.ups?.trackingStatus?.trim() ||
      null
    );
  }

  get isUpsDelivered(): boolean {
    return isOrderUpsDelivered(this.order, this.trackingDetails);
  }

  get canMarkPickedFromPrinter(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canMarkPickedFromPrinter);
  }

  get canForceSubmitToPrinter(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canForceSubmitToPrinter);
  }

  get isWaitingForOrderPdfs(): boolean {
    return Boolean(this.order?.fulfillment?.actions.isWaitingForOrderPdfs);
  }

  get isSubmittingToPrinter(): boolean {
    return Boolean(this.order?.fulfillment?.actions.isSubmittingToPrinter);
  }

  get showPrinterSubmitProgressPanel(): boolean {
    return (
      this.isSubmittingToPrinter ||
      this.isWaitingForOrderPdfs ||
      this.printerSubmitProgress?.phase === "failed"
    );
  }

  get printerOrderGroupId(): string | null {
    return this.order?.printerOrderGroupId?.trim() || null;
  }

  get hasRegisteredPrinterGroup(): boolean {
    return (
      Boolean(this.printerOrderGroupId) &&
      !this.order?.printer?.submittedAt?.trim()
    );
  }

  get printerOrderReferenceId(): string | null {
    return this.order?.printer?.orderReferenceId?.trim() || null;
  }

  get printerSubmitSectionBodyKey(): string {
    return this.hasRegisteredPrinterGroup
      ? "printCardOrders.fulfillment.printerSubmitSectionBodyResume"
      : "printCardOrders.fulfillment.printerSubmitSectionBody";
  }

  get printerSubmitProgress() {
    return (
      this.order?.fulfillment?.printerSubmitProgress ??
      this.order?.printer?.submitProgress ??
      null
    );
  }

  get printerSubmitPhaseLabelKey(): string {
    const progress = this.printerSubmitProgress;
    const step = progress?.step;
    if (progress?.phase === "linking") {
      if (step === "creating_group") {
        return "printCardOrders.fulfillment.printerSubmitStepCreatingGroup";
      }
      if (step === "matching_group") {
        return "printCardOrders.fulfillment.printerSubmitStepMatchingGroup";
      }
      if (step === "linking_items") {
        return "printCardOrders.fulfillment.printerSubmitStepLinkingItems";
      }
      return "printCardOrders.fulfillment.printerSubmitPhaseLinking";
    }
    if (progress?.phase === "uploading") {
      if (step === "rendering") {
        return "printCardOrders.fulfillment.printerSubmitStepRendering";
      }
      if (step === "uploading") {
        return "printCardOrders.fulfillment.printerSubmitStepUploading";
      }
      return "printCardOrders.fulfillment.printerSubmitPhaseUploading";
    }
    if (progress?.phase === "completed") {
      return "printCardOrders.fulfillment.printerSubmitPhaseCompleted";
    }
    if (progress?.phase === "failed") {
      return "printCardOrders.fulfillment.printerSubmitPhaseFailed";
    }
    return "printCardOrders.fulfillment.printerSubmitPhaseQueued";
  }

  get printerSubmitProgressPercent(): number {
    if (
      this.isWaitingForOrderPdfs &&
      !this.isSubmittingToPrinter &&
      this.pdfArchiveTotalCount > 0
    ) {
      return Math.min(
        100,
        Math.round(
          (this.pdfArchiveReadyCount / this.pdfArchiveTotalCount) * 100,
        ),
      );
    }
    const progress = this.printerSubmitProgress;
    if (!progress) {
      return 0;
    }
    if (progress.phase === "uploading" && progress.totalCount > 0) {
      return Math.min(
        100,
        Math.round((progress.uploadedCount / progress.totalCount) * 100),
      );
    }
    if (progress.phase === "completed") {
      return 100;
    }
    return 0;
  }

  get printerSubmitProgressBarDeterminate(): boolean {
    if (this.isWaitingForOrderPdfs && !this.isSubmittingToPrinter) {
      return this.pdfArchiveTotalCount > 0;
    }
    const phase = this.printerSubmitProgress?.phase;
    return phase === "uploading" || phase === "completed";
  }

  get showOrderPdfArchiveCount(): boolean {
    return (
      this.isWaitingForOrderPdfs &&
      !this.isSubmittingToPrinter &&
      this.pdfArchiveTotalCount > 0
    );
  }

  trackPrinterSubmitUploadedLabel(index: number, label: string): string {
    return `${index}:${label}`;
  }

  get showPrinterSubmitCount(): boolean {
    const progress = this.printerSubmitProgress;
    return Boolean(progress) && (progress?.totalCount ?? 0) > 0;
  }

  get printerSubmitProgressCountKey(): string {
    return this.printerSubmitProgress?.phase === "uploading"
      ? "printCardOrders.fulfillment.printerSubmitProgressCount"
      : "printCardOrders.fulfillment.printerSubmitProgressCountLinking";
  }

  get printerSubmitDetailTranslateKey(): string | null {
    const key = this.printerSubmitProgress?.detailKey?.trim();
    return key ? `printCardOrders.fulfillment.${key}` : null;
  }

  get printerSubmitDetailParams(): Record<string, string | number> {
    return this.printerSubmitProgress?.detailParams ?? {};
  }

  get printerSubmitErrorTranslateKey(): string | null {
    const progress = this.printerSubmitProgress;
    if (progress?.phase !== "failed") {
      return null;
    }
    const key = progress.lastErrorKey?.trim();
    return key ? `printCardOrders.fulfillment.${key}` : null;
  }

  get printerSubmitErrorParams(): Record<string, string | number> {
    return this.printerSubmitProgress?.lastErrorParams ?? {};
  }

  get scheduledPrinterSubmitAt(): string | null {
    if (this.order?.printer?.submittedAt?.trim()) {
      return null;
    }
    if (
      this.order?.status &&
      this.order.status !== "ORDER_PLACED" &&
      this.order.status !== "SENT_TO_PRINTER"
    ) {
      return null;
    }
    return this.order?.printer?.scheduledSubmitAt?.trim() || null;
  }

  get canMarkPacked(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canMarkPacked);
  }

  get canMarkDelivered(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canMarkDelivered);
  }

  get isMergedOrder(): boolean {
    return this.order?.merge?.role === "merged";
  }

  get mergedSourceCount(): number {
    if (this.order?.merge?.role !== "merged") {
      return 0;
    }
    return this.order.merge.sourcePrintCardOrderIds.length;
  }

  get hasCarrierShippingLabel(): boolean {
    if (this.hasExternalShipment) {
      return true;
    }
    const ups = this.order?.ups;
    if (ups?.trackingNumber?.trim()) {
      return true;
    }
    return (ups?.packages ?? []).some(
      (pkg) =>
        Boolean(pkg.trackingNumber?.trim()) ||
        Boolean(pkg.shippingLabelGifBase64?.trim()),
    );
  }

  get canUnmergeOrder(): boolean {
    return (
      this.isMergedOrder &&
      this.mergedSourceCount >= 2 &&
      !this.hasCarrierShippingLabel
    );
  }

  get showUnmergeBlockedByCarrier(): boolean {
    return this.isMergedOrder && this.hasCarrierShippingLabel;
  }

  get isPickupDeliveryOrder(): boolean {
    return this.order?.deliveryType === "PICKUP";
  }

  get nfcEncodingItems(): PrintCardNfcEncodingItem[] {
    if (!this.order) {
      return [];
    }
    const items: PrintCardNfcEncodingItem[] = [];
    const seenExternalIds = new Set<string>();
    for (const recipient of this.order.recipients) {
      if (
        recipient.fulfillmentBlockedReason ===
        FULFILLMENT_BLOCKED_REASON.EMPLOYEE_DELETED
      ) {
        continue;
      }
      for (const item of recipient.items) {
        const externalOrderId = item.externalOrderId?.trim();
        if (
          !externalOrderId ||
          seenExternalIds.has(externalOrderId) ||
          !isNfcPrintCardTemplateType(item.templateType)
        ) {
          continue;
        }
        seenExternalIds.add(externalOrderId);
        const encodedCount = printCardOrderItemEncodedCount(item);
        items.push({
          itemId: item.id,
          externalOrderId,
          printerOrderNumber:
            item.printer?.orderNumber?.trim() || externalOrderId,
          employeeName:
            recipient.employeeName?.trim() || recipient.employeeEmail,
          employeeEmail: recipient.employeeEmail,
          templateId: item.templateId,
          templateName: item.templateName,
          templateType: item.templateType,
          pageSizePresetId: item.pageSizePresetId,
          quantity: item.quantity,
          encodedCount,
          encoded: isPrintCardOrderItemNfcEncodingComplete(item),
          inProgress:
            encodedCount > 0 && !isPrintCardOrderItemNfcEncodingComplete(item),
          hasPdf: item.hasPdf,
        });
      }
    }
    return items;
  }

  trackNfcEncodingItem(_index: number, item: PrintCardNfcEncodingItem): string {
    return item.externalOrderId;
  }

  get showNfcEncodingSection(): boolean {
    return (
      (this.order?.status === "PICKED_FROM_PRINTER" ||
        this.order?.status === "PACKED") &&
      this.nfcEncodingItems.length > 0
    );
  }

  get pendingNfcEncodingCount(): number {
    return this.nfcEncodingItems.reduce(
      (remaining, item) =>
        remaining + Math.max(0, item.quantity - item.encodedCount),
      0,
    );
  }

  get canMarkNfcEncodedManually(): boolean {
    return (
      this.canDebugAdvanceStatus && this.order?.status === "PICKED_FROM_PRINTER"
    );
  }

  onNfcEncodedCheckboxChange(
    item: PrintCardNfcEncodingItem,
    checked: boolean,
  ): void {
    if (!checked || item.encoded || !this.canMarkNfcEncodedManually) {
      return;
    }
    this.markNfcItemEncoded(item);
  }

  private markNfcItemEncoded(item: PrintCardNfcEncodingItem): void {
    if (!this.order) {
      return;
    }

    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }
    this.nfcEncodingLoading = true;
    this.ordersService
      .setItemNfcEncoded(orderId, item.externalOrderId, item.quantity)
      .pipe(
        finalize(() => {
          this.nfcEncodingLoading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (order) => {
          this.applyOrderUpdate(order, { loadUpsPacking: true });
          this.toastr.success("printCardOrders.nfcEncoding.markEncodedSuccess");
        },
        error: () => {
          this.toastr.error("printCardOrders.nfcEncoding.markEncodedFailed");
        },
      });
  }

  get canDebugAdvanceStatus(): boolean {
    return Boolean(this.order?.fulfillment?.actions.canDebugAdvanceStatus);
  }

  get showDebugAdvanceHint(): boolean {
    const nextStepId = this.nextDebugTimelineStepId;
    if (!this.canDebugAdvanceStatus || !nextStepId) {
      return false;
    }
    return this.isTimelineStepClickable({
      id: nextStepId,
      state: "upcoming",
      occurredAt: null,
      detail: null,
      actor: null,
    });
  }

  get nextDebugTimelineStepId(): CardOrderStatus | null {
    if (!this.canDebugAdvanceStatus || !this.order?.fulfillment?.timeline) {
      return null;
    }
    const timeline = this.order.fulfillment.timeline;
    const currentIndex = timeline.findIndex((step) => step.state === "current");
    const anchorIndex =
      currentIndex >= 0
        ? currentIndex
        : timeline.reduce(
            (lastCompleted, step, index) =>
              step.state === "completed" ? index : lastCompleted,
            -1,
          );
    if (anchorIndex < 0) {
      return null;
    }
    const nextStep = timeline[anchorIndex + 1];
    return nextStep?.state === "upcoming" ? nextStep.id : null;
  }

  isTimelineStepClickable(step: PrintCardFulfillmentTimelineStep): boolean {
    if (
      !this.canDebugAdvanceStatus ||
      step.id !== this.nextDebugTimelineStepId
    ) {
      return false;
    }
    if (
      this.order?.status === "PENDING_APPROVAL" ||
      step.id === "SENT_TO_PRINTER"
    ) {
      return false;
    }
    return true;
  }

  onTimelineStepClick(step: PrintCardFulfillmentTimelineStep): void {
    if (!this.isTimelineStepClickable(step)) {
      return;
    }
    this.advanceFulfillmentDebug(step.id);
  }

  advanceFulfillment(
    action: "PICKED_FROM_PRINTER" | "PACKED" | "DELIVERED",
    upsPacking?: SavePrintCardOrderUpsPackingPayload,
  ): void {
    if (!this.order) {
      return;
    }

    const titleKey =
      action === "PICKED_FROM_PRINTER"
        ? "printCardOrders.fulfillment.confirmPickedTitle"
        : action === "DELIVERED"
          ? this.hasExternalShipment
            ? "printCardOrders.fulfillment.confirmExternalDeliveredTitle"
            : "printCardOrders.fulfillment.confirmDeliveredTitle"
          : "printCardOrders.fulfillment.confirmPackedTitle";
    const bodyKey =
      action === "PICKED_FROM_PRINTER"
        ? "printCardOrders.fulfillment.confirmPickedBody"
        : action === "DELIVERED"
          ? this.hasExternalShipment
            ? "printCardOrders.fulfillment.confirmExternalDeliveredBody"
            : "printCardOrders.fulfillment.confirmDeliveredBody"
          : this.isPickupDeliveryOrder
            ? "printCardOrders.fulfillment.confirmPackedPickupBody"
            : "printCardOrders.fulfillment.confirmPackedBody";

    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    const data: ConfirmationDialogParams = {
      title: titleKey,
      body: bodyKey,
      confirmButton: "confirm",
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "440px",
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.fulfillmentLoading = true;
        this.ordersService
          .advanceFulfillment(orderId, {
            action,
            ...(action === "PACKED" && upsPacking ? { upsPacking } : {}),
          })
          .pipe(
            finalize(() => {
              this.fulfillmentLoading = false;
            }),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (order) => {
              this.applyOrderUpdate(order, { loadUpsPacking: true });
              this.toastr.success(
                action === "PICKED_FROM_PRINTER"
                  ? "printCardOrders.fulfillment.pickedSuccess"
                  : action === "DELIVERED"
                    ? "printCardOrders.fulfillment.deliveredSuccess"
                    : "printCardOrders.fulfillment.packedSuccess",
              );
            },
            error: () => {
              this.toastr.error("printCardOrders.fulfillment.advanceFailed");
            },
          });
      });
  }

  unmergeOrder(): void {
    if (!this.order || !this.canUnmergeOrder || this.unmergeLoading) {
      return;
    }
    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "440px",
      data: {
        title: "printCardOrders.merge.unmergeTitle",
        body: "printCardOrders.merge.unmergeBody",
        confirmButton: "printCardOrders.merge.unmerge",
        translateParams: { count: this.mergedSourceCount },
      } satisfies ConfirmationDialogParams,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.unmergeLoading = true;
        this.ordersService
          .unmergeOrder(orderId)
          .pipe(
            finalize(() => {
              this.unmergeLoading = false;
            }),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: () => {
              this.toastr.success("printCardOrders.merge.unmergeSuccess");
              void this.router.navigate(["/admin/print-card-orders"]);
            },
            error: (error: { error?: { error?: string } }) => {
              const message = error?.error?.error?.trim();
              if (message) {
                this.toastr.error(message);
                return;
              }
              this.toastr.error("printCardOrders.merge.unmergeFailed");
            },
          });
      });
  }

  forceSubmitToPrinter(): void {
    if (!this.order || !this.canForceSubmitToPrinter) {
      return;
    }

    const data: ConfirmationDialogParams = {
      title: this.hasRegisteredPrinterGroup
        ? "printCardOrders.fulfillment.forceSubmitResumeTitle"
        : "printCardOrders.fulfillment.forceSubmitTitle",
      body: this.hasRegisteredPrinterGroup
        ? "printCardOrders.fulfillment.forceSubmitResumeBody"
        : "printCardOrders.fulfillment.forceSubmitBody",
      confirmButton: this.hasRegisteredPrinterGroup
        ? "printCardOrders.fulfillment.forceSubmitResumeConfirm"
        : "printCardOrders.fulfillment.forceSubmitConfirm",
      translateParams: this.hasRegisteredPrinterGroup
        ? {
            groupId: this.printerOrderGroupId || "—",
            referenceId: this.printerOrderReferenceId || "—",
          }
        : undefined,
    };
    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "440px",
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.fulfillmentLoading = true;
        this.ordersService
          .forceSubmitToPrinter(orderId)
          .pipe(
            finalize(() => {
              this.fulfillmentLoading = false;
            }),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (order) => {
              this.applyOrderUpdate(order, { loadUpsPacking: true });
            },
            error: (err: unknown) => {
              const apiMessage =
                err &&
                typeof err === "object" &&
                "error" in err &&
                err.error &&
                typeof err.error === "object" &&
                "error" in err.error &&
                typeof (err.error as { error?: unknown }).error === "string"
                  ? (err.error as { error: string }).error.trim()
                  : "";
              if (/pdf/i.test(apiMessage)) {
                this.toastr.error(
                  "printCardOrders.fulfillment.errors.pdfsNotReady",
                );
                return;
              }
              this.toastr.error(
                "printCardOrders.fulfillment.forceSubmitFailed",
              );
            },
          });
      });
  }

  private syncOrderPdfArchiveCountersFromOrder(): void {
    if (!this.order || this.isSubmittingToPrinter) {
      return;
    }
    let ready = 0;
    let total = 0;
    for (const recipient of this.order.recipients ?? []) {
      if (recipient.fulfillmentBlockedReason === "EMPLOYEE_DELETED") {
        continue;
      }
      for (const item of recipient.items ?? []) {
        total += 1;
        if (item.hasPdf) {
          ready += 1;
        }
      }
    }
    this.pdfArchiveReadyCount = ready;
    this.pdfArchiveTotalCount = total;
  }

  private syncDisplayedPrinterSubmitCounters(): void {
    const progress = this.printerSubmitProgress;
    const uploadedCount = this.showOrderPdfArchiveCount
      ? this.pdfArchiveReadyCount
      : Math.max(0, Math.floor(progress?.uploadedCount ?? 0));
    const percentage = this.printerSubmitProgressPercent;
    const animate =
      (this.isSubmittingToPrinter || this.isWaitingForOrderPdfs) &&
      progress?.phase !== "failed";
    const nextValues: Array<
      [
        "displayedPrinterSubmitUploadedCount" | "displayedPrinterSubmitPercent",
        number,
      ]
    > = [
      ["displayedPrinterSubmitUploadedCount", uploadedCount],
      ["displayedPrinterSubmitPercent", percentage],
    ];
    for (const [property, value] of nextValues) {
      if (this[property] === value) {
        continue;
      }
      if (animate) {
        this.animatePrinterSubmitCounter(property, this[property], value, 500);
      } else {
        const intervalId = this.printerSubmitCounterIntervals.get(property);
        if (intervalId) {
          clearInterval(intervalId);
          this.printerSubmitCounterIntervals.delete(property);
        }
        this[property] = value;
      }
    }
  }

  private animatePrinterSubmitCounter(
    property:
      "displayedPrinterSubmitUploadedCount" | "displayedPrinterSubmitPercent",
    from: number,
    to: number,
    duration = 500,
  ): void {
    const existing = this.printerSubmitCounterIntervals.get(property);
    if (existing) {
      clearInterval(existing);
      this.printerSubmitCounterIntervals.delete(property);
    }
    const steps = 20;
    const stepTime = duration / steps;
    const increment = (to - from) / steps;
    let currentStep = 0;
    const intervalId = setInterval(() => {
      currentStep += 1;
      this[property] = Math.round(from + increment * currentStep);
      if (currentStep >= steps) {
        clearInterval(intervalId);
        this.printerSubmitCounterIntervals.delete(property);
        this[property] = to;
      }
    }, stepTime);
    this.printerSubmitCounterIntervals.set(property, intervalId);
  }

  private syncPrinterSubmitPolling(): void {
    this.stopPrinterSubmitPolling$.next();
    if (!this.isSubmittingToPrinter && !this.isWaitingForOrderPdfs) {
      return;
    }
    const orderId = this.order?.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    timer(0, this.printerSubmitPollIntervalMs)
      .pipe(
        switchMap(() =>
          this.ordersService
            .getPrinterSubmitProgress(orderId)
            .pipe(catchError(() => of(null))),
        ),
        takeUntil(this.stopPrinterSubmitPolling$),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (snapshot) => {
          if (!snapshot || !this.order) {
            return;
          }
          const wasSubmitting = this.isSubmittingToPrinter;
          const progress = snapshot.submitProgress;
          this.pdfArchiveReadyCount = Math.max(
            0,
            Math.floor(snapshot.pdfReadyCount ?? this.pdfArchiveReadyCount),
          );
          this.pdfArchiveTotalCount = Math.max(
            0,
            Math.floor(snapshot.pdfTotalCount ?? this.pdfArchiveTotalCount),
          );
          this.order = {
            ...this.order,
            status: snapshot.status,
            printerOrderGroupId:
              snapshot.printerOrderGroupId ?? this.order.printerOrderGroupId,
            printer: {
              ...(this.order.printer ?? {
                statusId: null,
                statusLabel: null,
                orderNumbers: [],
                shipmentMethodTitle: null,
                endDate: null,
              }),
              submitProgress: progress,
              submittedAt: snapshot.submittedAt,
              submitQueuedAt: snapshot.submitQueuedAt,
            },
            fulfillment: this.order.fulfillment
              ? {
                  ...this.order.fulfillment,
                  status: snapshot.status,
                  printerSubmitProgress: progress,
                  actions: {
                    ...this.order.fulfillment.actions,
                    isSubmittingToPrinter: snapshot.isSubmittingToPrinter,
                    canForceSubmitToPrinter: snapshot.canForceSubmitToPrinter,
                    isWaitingForOrderPdfs: Boolean(
                      snapshot.isWaitingForOrderPdfs,
                    ),
                  },
                }
              : this.order.fulfillment,
          };
          this.syncDisplayedPrinterSubmitCounters();
          this.cdr.detectChanges();
          if (
            snapshot.isSubmittingToPrinter ||
            snapshot.isWaitingForOrderPdfs
          ) {
            return;
          }
          this.stopPrinterSubmitPolling$.next();
          this.ordersService
            .getOrder(orderId)
            .pipe(catchError(() => of(null)))
            .subscribe((order) => {
              if (order) {
                this.applyOrderUpdate(order, {
                  loadUpsPacking: false,
                  resetPackingEdit: false,
                  syncPrinterSubmitPolling: false,
                });
              }
              if (wasSubmitting && snapshot.submittedAt?.trim()) {
                this.toastr.success(
                  "printCardOrders.fulfillment.forceSubmitCompleted",
                );
              }
            });
        },
      });
  }

  private advanceFulfillmentDebug(targetStatus: CardOrderStatus): void {
    if (!this.order) {
      return;
    }

    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }
    this.fulfillmentLoading = true;
    this.ordersService
      .advanceFulfillment(orderId, { targetStatus })
      .pipe(
        finalize(() => {
          this.fulfillmentLoading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (order) => {
          this.applyOrderUpdate(order, { loadUpsPacking: true });
          this.toastr.success(
            "printCardOrders.fulfillment.debugAdvanceSuccess",
          );
        },
        error: () => {
          this.toastr.error("printCardOrders.fulfillment.advanceFailed");
        },
      });
  }

  markPackedWithUps(): void {
    const upsPacking = this.buildUpsPackingPayload();
    if (!upsPacking) {
      return;
    }
    this.advanceFulfillment("PACKED", upsPacking);
  }

  startUpsPackingEdit(): void {
    this.upsPackingEditing = true;
    this.syncUpsFormsLockState();
  }

  cancelUpsPackingEdit(): void {
    if (this.order) {
      this.loadUpsPackingFromOrder(this.order);
    }
    this.upsPackingEditing = false;
    this.syncUpsFormsLockState();
  }

  saveUpsPackingEdit(): void {
    if (!this.order) {
      return;
    }

    const payload = this.buildUpsPackingPayload();
    if (!payload) {
      return;
    }

    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    this.savingUpsPacking = true;
    this.ordersService
      .saveUpsPacking(orderId, payload)
      .pipe(
        finalize(() => {
          this.savingUpsPacking = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (order) => {
          this.applyOrderUpdate(order, { loadUpsPacking: true });
          this.toastr.success("printCardOrders.shipmentPackages.packingSaved");
        },
        error: () => {
          this.toastr.error(
            "printCardOrders.shipmentPackages.packingSaveFailed",
          );
        },
      });
  }

  onUpsPackagesChange(packages: PrintCardUpsPackageDraft[]): void {
    this.upsPackages = packages;
  }

  autoArrangeUpsPackages(): void {
    if (!this.upsPlanningActive) {
      return;
    }
    this.loadUpsPackPlan(true);
  }

  private loadUpsPackingFromOrder(order: PrintCardOrder, force = false): void {
    if (
      !order.printCardOrderId?.trim() ||
      this.hasUpsTracking ||
      !this.isUpsPackingSectionAvailable(order)
    ) {
      return;
    }

    if (this.orderHasSavedUpsPacking(order)) {
      this.upsPackages = getOrderUpsPackages<PrintCardUpsPackageDetails>(
        order,
      ).map((pkg) => this.toUpsPackageDraft(pkg));
      this.sanitizeUpsPackagesFromBlockedItems(order);

      const packing = order.ups?.packing;
      if (packing) {
        const currentShipment =
          this.shipmentForm.getRawValue() as UpsShipmentFormValue;
        const packingDcis = String(
          packing.deliveryConfirmation ??
            currentShipment.deliveryConfirmation ??
            "",
        ).trim();
        this.shipmentForm.patchValue(
          {
            serviceCode: packing.serviceCode ?? currentShipment.serviceCode,
            packagingCode:
              packing.packagingCode ?? currentShipment.packagingCode,
            shipmentDescription:
              packing.shipmentDescription ??
              currentShipment.shipmentDescription,
            weightUnit: packing.weightUnit ?? currentShipment.weightUnit,
            dimensionUnit:
              packing.dimensionUnit ?? currentShipment.dimensionUnit,
            deliveryConfirmation:
              packingDcis === "2" || packingDcis === "3" ? packingDcis : "",
            shipmentIndicationCode:
              packing.shipmentIndicationCode ??
              currentShipment.shipmentIndicationCode,
          },
          { emitEvent: false },
        );

        if (this.shipmentAccessorialsForm) {
          this.shipmentAccessorialsForm.patchValue(
            {
              directDeliveryOnly: packing.directDeliveryOnly ?? false,
              deliverToAddresseeOnly: packing.deliverToAddresseeOnly ?? false,
              dropoffAtUpsFacility: packing.dropoffAtUpsFacility ?? false,
            },
            { emitEvent: false },
          );
          this.syncIncompatibleAccessorials();
        }

        if (packing.internationalForms) {
          const forms = packing.internationalForms;
          this.internationalFormsForm.patchValue(
            {
              enabled: forms.enabled ?? false,
              formTypes: forms.formTypes ?? [],
              additionalDocumentIndicator:
                forms.additionalDocumentIndicator ?? false,
              invoiceNumber: forms.invoiceNumber ?? "",
              invoiceDate: forms.invoiceDate ?? "",
              purchaseOrderNumber: forms.purchaseOrderNumber ?? "",
              termsOfShipment: forms.termsOfShipment ?? "",
              reasonForExport: forms.reasonForExport ?? "",
              comments: forms.comments ?? "",
              declarationStatement: forms.declarationStatement ?? "",
              currencyCode: forms.currencyCode ?? "",
              productDescription: forms.productDescription ?? "",
              productCommodityCode: forms.productCommodityCode ?? "",
              productOriginCountry: forms.productOriginCountry ?? "",
              productUnitNumber: forms.productUnitNumber ?? "",
              productUnitOfMeasure: forms.productUnitOfMeasure ?? "",
              productUnitValue:
                forms.productUnitValue !== undefined &&
                forms.productUnitValue !== null
                  ? String(forms.productUnitValue)
                  : "",
            },
            { emitEvent: false },
          );
        }
      }

      this.loadUpsPackPlan(false, true);
      return;
    }

    if (force || this.upsPackages.length === 0) {
      this.loadUpsPackPlan(true);
    }
  }

  private orderHasSavedUpsPacking(order: PrintCardOrder): boolean {
    return getOrderUpsPackages<PrintCardUpsPackageDetails>(order).some(
      (pkg) => (pkg.printerOrderNumbers?.length ?? 0) > 0,
    );
  }

  get packerName(): string | null {
    return this.authService.getAppUser()?.fullName?.trim() || null;
  }

  private toUpsPackageDraft(
    pkg: PrintCardUpsPackageDetails | PrintCardUpsPackPackagePlan,
  ): PrintCardUpsPackageDraft {
    const packagingCode = String(
      this.shipmentForm.get("packagingCode")?.value ?? "",
    ).trim();
    return {
      id: pkg.id,
      printerOrderNumbers: [...(pkg.printerOrderNumbers ?? [])],
      packageWeight: Number(pkg.packageWeight ?? 0.5),
      packageLength: Number(pkg.packageLength ?? 0),
      packageWidth: Number(pkg.packageWidth ?? 0),
      packageHeight: Number(pkg.packageHeight ?? 0),
      weightUnit:
        (pkg.weightUnit as PrintCardUpsPackageDraft["weightUnit"]) ?? "KGS",
      dimensionUnit:
        (pkg.dimensionUnit as PrintCardUpsPackageDraft["dimensionUnit"]) ??
        "CM",
      packagingCode: pkg.packagingCode?.trim() || packagingCode,
      packageDescription:
        pkg.packageDescription?.trim() ||
        createUpsPackageDescription(this.packerName),
    };
  }

  private syncUpsFormsLockState(): void {
    if (this.upsFormsLocked) {
      this.shipmentForm.disable({ emitEvent: false });
      this.shipmentAccessorialsForm?.disable({ emitEvent: false });
      this.internationalFormsForm.disable({ emitEvent: false });
      this.notificationForm.disable({ emitEvent: false });
    } else {
      this.shipmentForm.enable({ emitEvent: false });
      this.shipmentAccessorialsForm?.enable({ emitEvent: false });
      this.internationalFormsForm.enable({ emitEvent: false });
      this.notificationForm.enable({ emitEvent: false });
      this.syncIncompatibleAccessorials();
      if (!this.isInternationalUpsShipment) {
        this.internationalFormsForm
          .get("enabled")
          ?.disable({ emitEvent: false });
      }
    }
  }

  private mapPackagesToPayload(): SavePrintCardOrderUpsPackingPayload["packages"] {
    const packagingCode = String(
      this.shipmentForm.get("packagingCode")?.value ?? "",
    ).trim();
    const shipment = this.shipmentForm.getRawValue() as UpsShipmentFormValue;
    const weightUnit = shipment.weightUnit === "LBS" ? "LBS" : "KGS";
    const dimensionUnit = weightUnit === "LBS" ? "IN" : "CM";
    return this.upsPackages.map((pkg) => ({
      id: pkg.id,
      printerOrderNumbers: pkg.printerOrderNumbers,
      packagingCode: pkg.packagingCode?.trim() || packagingCode,
      packageDescription: pkg.packageDescription || undefined,
      packageWeight: Number(pkg.packageWeight),
      weightUnit,
      packageLength: Number(pkg.packageLength),
      packageWidth: Number(pkg.packageWidth),
      packageHeight: Number(pkg.packageHeight),
      dimensionUnit,
    }));
  }

  private appendAccessorialsAndInternationalForms(payload: {
    shipmentAccessorials?: UpsShipmentAccessorialsPayload;
    internationalForms?: ShipPrintCardOrderUpsPayload["internationalForms"];
  }): void {
    if (this.shipmentAccessorialsForm) {
      const shipmentAccessorials = this.getShipmentAccessorials(
        this.shipmentAccessorialsForm,
      );
      if (shipmentAccessorials) {
        payload.shipmentAccessorials = shipmentAccessorials;
      }
    }

    const international =
      this.internationalFormsForm.getRawValue() as UpsInternationalFormsFormValue;
    if (!international.enabled) {
      return;
    }

    const productUnitValue = Number(international.productUnitValue);
    payload.internationalForms = {
      enabled: true,
      formTypes: international.formTypes ?? [],
      additionalDocumentIndicator:
        international.additionalDocumentIndicator || undefined,
      invoiceNumber: international.invoiceNumber || undefined,
      invoiceDate: international.invoiceDate || undefined,
      purchaseOrderNumber: international.purchaseOrderNumber || undefined,
      termsOfShipment: international.termsOfShipment || undefined,
      reasonForExport: international.reasonForExport || undefined,
      comments: international.comments || undefined,
      declarationStatement: international.declarationStatement || undefined,
      currencyCode: international.currencyCode.trim() || undefined,
      productDescription: international.productDescription || undefined,
      productCommodityCode: international.productCommodityCode || undefined,
      productOriginCountry: international.productOriginCountry || undefined,
      productUnitNumber: international.productUnitNumber.trim() || undefined,
      productUnitOfMeasure:
        international.productUnitOfMeasure.trim() || undefined,
      ...(Number.isFinite(productUnitValue) ? { productUnitValue } : {}),
    };
  }

  private buildUpsPackingPayload(): SavePrintCardOrderUpsPackingPayload | null {
    this.markValidationAttempt();

    if (
      this.isShipmentDetailsInvalid() ||
      this.internationalFormsForm.invalid
    ) {
      this.toastr.error("printCardOrders.shipmentFormValidationFailed");
      return null;
    }

    if (!this.upsPackagePlanner?.isValid()) {
      this.toastr.error("printCardOrders.shipmentPackages.validation");
      return null;
    }

    const shipment = this.shipmentForm.getRawValue() as UpsShipmentFormValue;
    const packagingCode = shipment.packagingCode.trim();
    if (!packagingCode) {
      this.toastr.error("printCardOrders.shipmentFormValidationFailed");
      return null;
    }
    if (!shipment.shipmentDescription.trim()) {
      this.toastr.error("printCardOrders.shipmentFormValidationFailed");
      return null;
    }

    const packingDeliveryConfirmation = String(
      shipment.deliveryConfirmation ?? "",
    ).trim();
    const payload: SavePrintCardOrderUpsPackingPayload = {
      serviceCode: shipment.serviceCode,
      packagingCode,
      shipmentDescription: shipment.shipmentDescription.trim(),
      weightUnit: shipment.weightUnit,
      dimensionUnit: shipment.dimensionUnit,
      deliveryConfirmation: this.isAccessPointDelivery
        ? ""
        : packingDeliveryConfirmation === "2" ||
            packingDeliveryConfirmation === "3"
          ? packingDeliveryConfirmation
          : "",
      packages: this.mapPackagesToPayload(),
    };

    if (this.isAccessPointDelivery) {
      const indication = shipment.shipmentIndicationCode.trim();
      if (indication !== "01" && indication !== "02") {
        this.toastr.error("printCardOrders.shipmentFormValidationFailed");
        return null;
      }
      payload.shipmentIndicationCode = indication;
    }

    this.appendAccessorialsAndInternationalForms(payload);

    return payload;
  }

  private sanitizeUpsPackagesFromBlockedItems(order: PrintCardOrder): void {
    const blocked = getBlockedPrinterOrderNumbers(order.recipients);
    if (blocked.size === 0) {
      return;
    }
    this.upsPackages = this.upsPackages.map((pkg) => ({
      ...pkg,
      printerOrderNumbers: pkg.printerOrderNumbers.filter(
        (number) => !blocked.has(number.trim()),
      ),
    }));
  }

  private loadUpsPackPlan(force = false, lineItemsOnly = false): void {
    const orderId = this.order?.printCardOrderId?.trim();
    if (
      !orderId ||
      this.hasUpsTracking ||
      !this.isUpsPackingSectionAvailable()
    ) {
      return;
    }
    if (!force && !lineItemsOnly && this.upsPackages.length > 0) {
      return;
    }

    this.loadingPackPlan = true;
    this.ordersService
      .getUpsPackPlan(orderId)
      .pipe(
        finalize(() => {
          this.loadingPackPlan = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (plan) => {
          this.upsPackLineItems = plan.items;
          if (!lineItemsOnly) {
            this.upsPackages = plan.packages.map((pkg) =>
              this.toUpsPackageDraft(pkg),
            );
          } else if (this.order) {
            this.sanitizeUpsPackagesFromBlockedItems(this.order);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastr.error("printCardOrders.shipmentPackages.planFailed");
        },
      });
  }

  submitUpsShipment(): void {
    if (!this.order || this.hasUpsTracking) {
      return;
    }

    const payload = this.buildShipmentPayload();
    if (!payload) {
      return;
    }

    const data: ConfirmationDialogParams = {
      title: "printCardOrders.shipConfirmTitle",
      body: "printCardOrders.shipmentPackages.shipConfirmBody",
      confirmButton: "printCardOrders.submitShipment",
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "440px",
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.executeUpsShipment(payload);
      });
  }

  private executeUpsShipment(payload: ShipPrintCardOrderUpsPayload): void {
    if (!this.order) {
      return;
    }

    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    this.shipping = true;
    this.ordersService
      .shipWithUps(orderId, payload)
      .pipe(
        finalize(() => {
          this.shipping = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (order) => {
          this.applyOrderUpdate(order, { loadUpsPacking: false });
          if (order.warnings?.pickupFailed) {
            this.toastr.warning("printCardOrders.shipSuccessPickupFailed");
          } else {
            this.toastr.success("printCardOrders.shipSuccess");
          }
          this.openShippingLabelsDialog(order);
        },
        error: (error) => {
          this.markValidationAttempt();
          this.toastr.apiError(error, "cardOrders.shipmentErrors.rejected");
        },
      });
  }

  private openShippingLabelsDialog(order: PrintCardOrder): void {
    const labels: UpsShippingLabelDialogItem[] =
      getOrderUpsPackages<PrintCardUpsPackageDetails>(order)
        .map((pkg, index) => ({
          index: index + 1,
          trackingNumber: pkg.trackingNumber,
          packageDescription: pkg.packageDescription,
          shippingLabelGifBase64: pkg.shippingLabelGifBase64?.trim() ?? "",
        }))
        .filter((item) => item.shippingLabelGifBase64.length > 0);

    if (labels.length === 0) {
      return;
    }

    this.dialog.open(UpsShippingLabelsDialogComponent, {
      width: "960px",
      maxWidth: "96vw",
      panelClass: "app-dialog-panel",
      data: { labels },
    });
  }

  openAttachShipmentDialog(): void {
    if (!this.order || !this.canAttachManualShipment) {
      return;
    }
    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    this.dialog
      .open<
        AttachShipmentDialogComponent,
        AttachShipmentDialogData,
        PrintCardOrder | undefined
      >(AttachShipmentDialogComponent, {
        width: "520px",
        maxWidth: "96vw",
        panelClass: "app-dialog-panel",
        autoFocus: "dialog",
        data: { orderId },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((order) => {
        if (order) {
          this.applyOrderUpdate(order, { loadUpsPacking: false });
        }
      });
  }

  openUpsShopRatesDialog(): void {
    if (!this.order || !this.canEstimateUpsShippingCost) {
      return;
    }

    if (this.isInternationalUpsShipment) {
      this.patchInternationalFormsDefaultsIfEmpty(this.order);
      if (!this.internationalFormsForm.get("enabled")?.value) {
        this.internationalFormsForm.patchValue(
          { enabled: true },
          { emitEvent: false },
        );
        this.updateInternationalValidators(true);
        this.patchInternationalFormsDefaultsIfEmpty(this.order);
      }
    }

    const payload = this.buildShopRatesPayload();
    if (!payload) {
      return;
    }

    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    const totalWeightKg = (payload.packages ?? []).reduce(
      (sum, pkg) => sum + Number(pkg.packageWeight || 0),
      0,
    );
    const weightUnit =
      payload.packages?.[0]?.weightUnit || payload.weightUnit || "KGS";

    this.dialog.open(UpsShopRatesDialogComponent, {
      width: "920px",
      maxWidth: "96vw",
      panelClass: "app-dialog-panel",
      autoFocus: "dialog",
      data: {
        orderId,
        payload,
        totalWeightKg: totalWeightKg > 0 ? totalWeightKg : 0.5,
        weightUnit,
      },
    });
  }

  private buildShopRatesPayload(): ShipPrintCardOrderUpsPayload | null {
    if (this.showUpsPackingSection && this.upsPackagePlanner?.isValid()) {
      const packedPayload = this.buildShipmentPayload();
      if (packedPayload?.packages?.length) {
        packedPayload.pickup = { enabled: false };
        return packedPayload;
      }
    }

    this.errorStateMatcher.submitAttempted = true;
    this.shipperForm.markAllAsTouched();
    this.recipientForm.markAllAsTouched();
    this.shipmentForm.markAllAsTouched();

    const recipient = this.recipientForm.getRawValue() as UpsRecipientFormValue;
    const shipper = this.shipperForm.getRawValue() as UpsShipperFormValue;
    const shipment = this.shipmentForm.getRawValue() as UpsShipmentFormValue;
    const deliveryType = recipient.deliveryType;

    if (deliveryType !== "COURIER" && deliveryType !== "ACCESS_POINT") {
      this.toastr.error("printCardOrders.shopRates.upsDeliveryRequired");
      return null;
    }
    if (this.shipperForm.invalid || this.recipientForm.invalid) {
      this.toastr.error("printCardOrders.shipmentPartiesValidationFailed");
      return null;
    }
    if (deliveryType === "ACCESS_POINT" && !recipient.accessPointId?.trim()) {
      this.toastr.error("printCardOrders.shipmentFormValidationFailed");
      return null;
    }

    const fromPackages = this.upsPackages.flatMap(
      (pkg) => pkg.printerOrderNumbers,
    );
    const fromItems = (this.order?.recipients ?? []).flatMap((recipient) =>
      recipient.items.map(
        (item) =>
          item.printerOrderNumber?.trim() ||
          item.externalOrderId?.trim() ||
          item.id.trim(),
      ),
    );
    const printerOrderNumbers = [
      ...new Set(
        (fromPackages.length > 0 ? fromPackages : fromItems)
          .map((v) => v.trim())
          .filter(Boolean),
      ),
    ];
    if (printerOrderNumbers.length === 0) {
      this.toastr.error("printCardOrders.shopRates.noOrderItems");
      return null;
    }

    const weightUnit: UpsWeightUnit =
      shipment.weightUnit === "LBS" ? "LBS" : "KGS";
    const dimensionUnit = weightUnit === "LBS" ? "IN" : "CM";
    const totalWeight = this.upsPackages.reduce(
      (sum, pkg) => sum + Number(pkg.packageWeight || 0),
      0,
    );
    const packageWeight =
      totalWeight > 0
        ? Math.round(totalWeight * 100) / 100
        : Number(this.upsOptions?.defaults.packageWeight ?? 0.5);

    const payload: ShipPrintCardOrderUpsPayload = {
      shipper: {
        name: shipper.name.trim(),
        line1: shipper.line1.trim(),
        ...(shipper.line2?.trim() ? { line2: shipper.line2.trim() } : {}),
        city: shipper.city.trim(),
        postalCode: shipper.postalCode.trim(),
        country: shipper.country,
        phone: shipper.phone,
      },
      recipientName: recipient.recipientName.trim(),
      ...(recipient.companyName.trim()
        ? { companyName: recipient.companyName.trim() }
        : {}),
      phone: recipient.phone,
      shippingAddress: {
        line1: recipient.line1,
        line2: recipient.line2 || undefined,
        city: recipient.city,
        postalCode: recipient.postalCode,
        country: recipient.country,
      },
      addressLocationType: recipient.addressLocationType,
      deliveryType,
      serviceCode:
        shipment.serviceCode || this.upsOptions?.defaults.serviceCode || "11",
      packagingCode:
        shipment.packagingCode ||
        this.upsOptions?.defaults.packagingCode ||
        "02",
      shipmentDescription: shipment.shipmentDescription.trim() || "Print cards",
      ratingRequestOption: "Ratetimeintransit",
      packageWeight,
      weightUnit,
      dimensionUnit,
      deliveryConfirmation: "",
      packages: [
        {
          printerOrderNumbers,
          packageWeight,
          weightUnit,
          packageLength: 30,
          packageWidth: 22,
          packageHeight: 10,
          dimensionUnit,
          packagingCode: shipment.packagingCode || "02",
        },
      ],
      pickup: { enabled: false },
    };

    if (deliveryType === "ACCESS_POINT") {
      payload.upsAccessPoint = {
        id: recipient.accessPointId,
        name: recipient.accessPointName,
        line1: recipient.accessPointLine1,
        city: recipient.accessPointCity,
        postalCode: recipient.accessPointPostalCode,
        country: recipient.accessPointCountry || recipient.country,
      };
      const indication = String(shipment.shipmentIndicationCode || "01").trim();
      payload.shipmentIndicationCode = indication === "02" ? "02" : "01";
    }

    this.appendAccessorialsAndInternationalForms(payload);
    return payload;
  }

  saveShipmentParties(): void {
    if (
      !this.order ||
      this.hasUpsTracking ||
      this.order.status === "DELIVERED" ||
      !this.shipmentPartiesDirty
    ) {
      return;
    }

    this.partiesErrorStateMatcher.submitAttempted = true;
    this.recipientForm.markAllAsTouched();
    const deliveryType = (
      this.recipientForm.getRawValue() as UpsRecipientFormValue
    ).deliveryType;
    if (deliveryType !== "PICKUP") {
      this.shipperForm.markAllAsTouched();
    }

    if (
      this.recipientForm.invalid ||
      (deliveryType !== "PICKUP" && this.shipperForm.invalid)
    ) {
      this.toastr.error("printCardOrders.shipmentPartiesValidationFailed");
      return;
    }

    const payload = this.buildShipmentPartiesPayload();
    if (!payload) {
      return;
    }

    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }

    this.savingParties = true;
    this.ordersService
      .updateShipment(orderId, payload)
      .pipe(
        finalize(() => {
          this.savingParties = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (order) => {
          this.order = order;
          this.applyShipperFromOrderOrDefaults(order, null);
          this.populateRecipientForm(order);
          this.capturePartiesBaseline();
          this.partiesErrorStateMatcher.submitAttempted = false;
          this.toastr.success("printCardOrders.shipmentPartiesSaveSuccess");
        },
        error: (error) => {
          this.toastr.apiError(
            error,
            "printCardOrders.shipmentPartiesSaveFailed",
          );
        },
      });
  }

  private markValidationAttempt(): void {
    this.errorStateMatcher.submitAttempted = true;
    this.partiesErrorStateMatcher.submitAttempted = true;
    this.shipperForm.markAllAsTouched();
    this.recipientForm.markAllAsTouched();
    this.shipmentForm.markAllAsTouched();
    this.pickupScheduleForm.markAllAsTouched();
    this.internationalFormsForm.markAllAsTouched();
    this.notificationForm.markAllAsTouched();
  }

  private buildShipmentPayload(): ShipPrintCardOrderUpsPayload | null {
    if (!this.order) {
      return null;
    }

    this.markValidationAttempt();

    if (
      this.shipperForm.invalid ||
      this.recipientForm.invalid ||
      this.isShipmentDetailsInvalid() ||
      this.internationalFormsForm.invalid ||
      this.notificationForm.invalid
    ) {
      this.toastr.error("printCardOrders.shipmentFormValidationFailed");
      return null;
    }

    const shipper = this.shipperForm.getRawValue() as UpsShipperFormValue;
    const recipient = this.recipientForm.getRawValue() as UpsRecipientFormValue;
    const shipment = this.shipmentForm.getRawValue() as UpsShipmentFormValue;
    const deliveryType = recipient.deliveryType;
    const shipmentDcis = String(shipment.deliveryConfirmation ?? "").trim();

    const payload: ShipPrintCardOrderUpsPayload = {
      shipper: {
        name: shipper.name,
        line1: shipper.line1,
        line2: shipper.line2 || undefined,
        city: shipper.city,
        postalCode: shipper.postalCode,
        country: shipper.country,
        phone: shipper.phone,
      },
      recipientName: recipient.recipientName.trim(),
      ...(recipient.companyName.trim()
        ? { companyName: recipient.companyName.trim() }
        : {}),
      phone: recipient.phone,
      shippingAddress: {
        line1: recipient.line1,
        line2: recipient.line2 || undefined,
        city: recipient.city,
        postalCode: recipient.postalCode,
        country: recipient.country,
      },
      addressLocationType: recipient.addressLocationType,
      deliveryType,
      serviceCode: shipment.serviceCode,
      packagingCode: shipment.packagingCode,
      shipmentDescription: shipment.shipmentDescription.trim(),
      ratingRequestOption: shipment.ratingRequestOption,
      packageWeight: this.upsPackages.reduce(
        (sum, pkg) => sum + Number(pkg.packageWeight || 0),
        0,
      ),
      weightUnit: shipment.weightUnit,
      dimensionUnit: shipment.dimensionUnit,
      deliveryConfirmation:
        deliveryType === "ACCESS_POINT"
          ? ""
          : shipmentDcis === "2" || shipmentDcis === "3"
            ? shipmentDcis
            : "",
    };

    if (deliveryType === "ACCESS_POINT") {
      payload.upsAccessPoint = {
        id: recipient.accessPointId,
        name: recipient.accessPointName,
        line1: recipient.accessPointLine1,
        city: recipient.accessPointCity,
        postalCode: recipient.accessPointPostalCode,
        country: recipient.accessPointCountry || recipient.country,
        ...(recipient.accessPointOpeningHours.trim()
          ? { openingHours: recipient.accessPointOpeningHours.trim() }
          : {}),
      };
      const indication = shipment.shipmentIndicationCode.trim();
      if (indication !== "01" && indication !== "02") {
        this.toastr.error("printCardOrders.shipmentFormValidationFailed");
        return null;
      }
      payload.shipmentIndicationCode = indication;
    }

    this.appendAccessorialsAndInternationalForms(payload);
    const notification =
      this.notificationForm.getRawValue() as UpsNotificationFormValue;
    const builtNotifications = this.buildShipmentNotifications(notification);
    if (builtNotifications.length > 0) {
      payload.notifications = builtNotifications;
    }

    if (!this.upsPackagePlanner?.isValid()) {
      this.toastr.error("printCardOrders.shipmentPackages.validation");
      return null;
    }

    payload.packages = this.mapPackagesToPayload();

    const pickupDate = this.toUpsPickupDate(shipment.pickupDate);
    const readyTime = this.toUpsPickupTime(shipment.readyTime);
    const closeTime = this.toUpsPickupTime(shipment.closeTime);
    const pickupPoint = this.asTrimmedString(shipment.pickupPoint);
    const specialInstruction = this.asTrimmedString(
      shipment.specialInstruction,
    );
    payload.pickup = {
      enabled: shipment.pickupEnabled !== false,
      ...(pickupDate ? { pickupDate } : {}),
      ...(readyTime ? { readyTime } : {}),
      ...(closeTime ? { closeTime } : {}),
      ...(pickupPoint ? { pickupPoint } : {}),
      ...(specialInstruction ? { specialInstruction } : {}),
    };

    return payload;
  }

  private defaultPickupDate(): unknown {
    return this.dateAdapter.today();
  }

  private defaultPickupTime(hour: number, minute: number): unknown {
    return this.dateAdapter.setTime(this.dateAdapter.today(), hour, minute, 0);
  }

  private toUpsPickupDate(value: unknown): string | undefined {
    if (value == null || value === "") {
      return undefined;
    }

    if (
      this.dateAdapter.isDateInstance(value) &&
      this.dateAdapter.isValid(value)
    ) {
      const year = this.dateAdapter.getYear(value);
      const month = String(this.dateAdapter.getMonth(value) + 1).padStart(
        2,
        "0",
      );
      const day = String(this.dateAdapter.getDate(value)).padStart(2, "0");
      return `${year}${month}${day}`;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return undefined;
      }
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}${month}${day}`;
    }

    const trimmed = typeof value === "string" ? value.trim() : "";
    if (/^\d{8}$/.test(trimmed)) {
      return trimmed;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (!match) {
      return undefined;
    }
    return `${match[1]}${match[2]}${match[3]}`;
  }

  private toUpsPickupTime(value: unknown): string | undefined {
    if (value == null || value === "") {
      return undefined;
    }

    if (
      this.dateAdapter.isDateInstance(value) &&
      this.dateAdapter.isValid(value)
    ) {
      const hours = String(this.dateAdapter.getHours(value)).padStart(2, "0");
      const minutes = String(this.dateAdapter.getMinutes(value)).padStart(
        2,
        "0",
      );
      return `${hours}${minutes}`;
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return undefined;
      }
      const hours = String(value.getHours()).padStart(2, "0");
      const minutes = String(value.getMinutes()).padStart(2, "0");
      return `${hours}${minutes}`;
    }

    const trimmed = typeof value === "string" ? value.trim() : "";
    if (/^\d{4}$/.test(trimmed)) {
      return trimmed;
    }
    const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
    if (!match) {
      return undefined;
    }
    return `${match[1].padStart(2, "0")}${match[2]}`;
  }

  private asTrimmedString(value: unknown): string {
    return typeof value === "string" ? value.trim() : "";
  }

  scheduleUpsPickup(): void {
    if (!this.order || !this.canScheduleUpsPickup) {
      return;
    }
    let payload: SchedulePrintCardOrderUpsPickupPayload | null = null;
    try {
      payload = this.buildPickupSchedulePayload();
    } catch {
      this.toastr.error(
        "printCardOrders.shipment.pickupScheduleValidationFailed",
      );
      return;
    }
    if (!payload) {
      return;
    }
    const orderId = this.order.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }
    this.shipping = true;
    this.ordersService
      .scheduleUpsPickup(orderId, payload)
      .pipe(
        finalize(() => {
          this.shipping = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (order) => {
          this.applyOrderUpdate(order, { loadUpsPacking: false });
          this.toastr.success("printCardOrders.shipment.pickupScheduleSuccess");
        },
        error: (error) => {
          this.toastr.apiError(
            error,
            "printCardOrders.shipment.pickupScheduleFailed",
          );
        },
      });
  }

  private buildPickupSchedulePayload(): SchedulePrintCardOrderUpsPickupPayload | null {
    if (!this.order) {
      return null;
    }

    this.markValidationAttempt();
    const shipper = this.shipperForm.getRawValue() as UpsShipperFormValue;
    const shipment = this.shipmentForm.getRawValue() as UpsShipmentFormValue;
    const recipient = this.recipientForm.getRawValue() as UpsRecipientFormValue;
    const pickup =
      this.pickupScheduleForm.getRawValue() as UpsPickupScheduleFormValue;

    const pickupDate = this.toUpsPickupDate(pickup.pickupDate);
    const readyTime = this.toUpsPickupTime(pickup.readyTime);
    const closeTime = this.toUpsPickupTime(pickup.closeTime);
    if (!pickupDate || !readyTime || !closeTime) {
      this.toastr.error(
        "printCardOrders.shipment.pickupScheduleValidationFailed",
      );
      return null;
    }
    if (readyTime >= closeTime) {
      this.toastr.error(
        "printCardOrders.shipment.pickupScheduleValidationFailed",
      );
      return null;
    }

    const orderShipper = this.order.shipper;
    const shipperName =
      this.asTrimmedString(shipper.name) ||
      this.asTrimmedString(orderShipper?.name);
    const shipperLine1 =
      this.asTrimmedString(shipper.line1) ||
      this.asTrimmedString(orderShipper?.line1);
    const shipperLine2 =
      this.asTrimmedString(shipper.line2) ||
      this.asTrimmedString(orderShipper?.line2);
    const shipperCity =
      this.asTrimmedString(shipper.city) ||
      this.asTrimmedString(orderShipper?.city);
    const shipperPostal =
      this.asTrimmedString(shipper.postalCode) ||
      this.asTrimmedString(orderShipper?.postalCode);
    const shipperPhone =
      this.asTrimmedString(shipper.phone) ||
      this.asTrimmedString(orderShipper?.phone);
    const shipperCountry =
      this.asTrimmedString(shipper.country) ||
      this.asTrimmedString(orderShipper?.country) ||
      "PL";
    if (
      !shipperName ||
      !shipperLine1 ||
      !shipperCity ||
      !shipperPostal ||
      !shipperPhone
    ) {
      this.toastr.error(
        "printCardOrders.shipment.pickupScheduleValidationFailed",
      );
      return null;
    }

    const packages = getOrderUpsPackages<PrintCardUpsPackageDetails>(
      this.order,
    );
    const totalWeight = packages.reduce(
      (sum, pkg) => sum + Number(pkg.packageWeight || 0),
      0,
    );
    if (!(totalWeight > 0)) {
      this.toastr.error(
        "printCardOrders.shipment.pickupScheduleValidationFailed",
      );
      return null;
    }

    const serviceCode =
      this.asTrimmedString(this.order.ups?.packing?.serviceCode) ||
      this.asTrimmedString(shipment.serviceCode) ||
      this.asTrimmedString(this.upsOptions?.defaults.serviceCode);
    if (!serviceCode) {
      this.toastr.error(
        "printCardOrders.shipment.pickupScheduleValidationFailed",
      );
      return null;
    }

    const weightUnit = (this.asTrimmedString(packages[0]?.weightUnit) ||
      this.asTrimmedString(shipment.weightUnit) ||
      "KGS") as UpsWeightUnit;
    const destinationCountryCode = upsCountryCode(
      this.order.shippingAddress.country || recipient.country || "PL",
    );
    const pickupPoint = this.asTrimmedString(pickup.pickupPoint);
    const specialInstruction = this.asTrimmedString(pickup.specialInstruction);

    return {
      shipper: {
        name: shipperName,
        line1: shipperLine1,
        ...(shipperLine2 ? { line2: shipperLine2 } : {}),
        city: shipperCity,
        postalCode: shipperPostal,
        country: upsCountryCode(shipperCountry),
        phone: shipperPhone,
      },
      addressLocationType: recipient.addressLocationType || "COMMERCIAL",
      destinationCountryCode,
      serviceCode,
      weightUnit: weightUnit === "LBS" ? "LBS" : "KGS",
      pickup: {
        pickupDate,
        readyTime,
        closeTime,
        ...(pickupPoint ? { pickupPoint } : {}),
        ...(specialInstruction ? { specialInstruction } : {}),
      },
    };
  }

  cancelUpsPickup(): void {
    if (!this.order || !this.canCancelUpsPickup) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "440px",
      data: {
        title: "printCardOrders.shipment.pickupCancelTitle",
        body: "printCardOrders.shipment.pickupCancelBody",
        confirmButton: "printCardOrders.shipment.pickupCancelConfirm",
      } satisfies ConfirmationDialogParams,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed || !this.order) {
          return;
        }
        const orderId = this.order.printCardOrderId?.trim();
        if (!orderId) {
          return;
        }
        this.shipping = true;
        this.ordersService
          .cancelUpsPickup(orderId)
          .pipe(
            finalize(() => {
              this.shipping = false;
            }),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (order) => {
              this.applyOrderUpdate(order, { loadUpsPacking: false });
              this.toastr.success(
                "printCardOrders.shipment.pickupCancelSuccess",
              );
            },
            error: (error) => {
              this.toastr.apiError(
                error,
                "printCardOrders.shipment.pickupCancelFailed",
              );
            },
          });
      });
  }

  voidUpsShipment(): void {
    if (!this.order || !this.canVoidUpsShipment) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "440px",
      data: {
        title: "printCardOrders.shipment.voidTitle",
        body: "printCardOrders.shipment.voidBody",
        confirmButton: "printCardOrders.shipment.voidConfirm",
      } satisfies ConfirmationDialogParams,
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed || !this.order) {
          return;
        }
        const orderId = this.order.printCardOrderId?.trim();
        if (!orderId) {
          return;
        }
        this.shipping = true;
        this.ordersService
          .voidUpsShipment(orderId)
          .pipe(
            finalize(() => {
              this.shipping = false;
            }),
            takeUntil(this.destroy$),
          )
          .subscribe({
            next: (order) => {
              this.applyOrderUpdate(order, { loadUpsPacking: true });
              this.toastr.success("printCardOrders.shipment.voidSuccess");
            },
            error: (error) => {
              this.toastr.apiError(
                error,
                "printCardOrders.shipment.voidFailed",
              );
            },
          });
      });
  }

  private isShipmentDetailsInvalid(): boolean {
    const controls = [
      "serviceCode",
      "packagingCode",
      "shipmentDescription",
      "ratingRequestOption",
      "weightUnit",
      "dimensionUnit",
    ];
    if (this.isAccessPointDelivery) {
      controls.push("shipmentIndicationCode");
    }
    const shipment = this.shipmentForm.getRawValue() as UpsShipmentFormValue;
    if (shipment.pickupEnabled !== false) {
      controls.push("pickupDate", "readyTime", "closeTime");
    }
    return controls.some((name) => this.shipmentForm.get(name)?.invalid);
  }

  private setupPartiesDirtyTracking(): void {
    merge(this.shipperForm.valueChanges, this.recipientForm.valueChanges)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePartiesDirtyState();
      });
  }

  private capturePartiesBaseline(): void {
    this.partiesBaselineJson = this.serializePartiesState();
    this.shipmentPartiesDirty = false;
  }

  private updatePartiesDirtyState(): void {
    if (!this.partiesBaselineJson) {
      this.shipmentPartiesDirty = false;
      return;
    }
    this.shipmentPartiesDirty =
      this.serializePartiesState() !== this.partiesBaselineJson;
  }

  private serializePartiesState(): string {
    return JSON.stringify({
      shipper: this.shipperForm.getRawValue() as UpsShipperFormValue,
      recipient: this.recipientForm.getRawValue() as UpsRecipientFormValue,
    });
  }

  openAccessPointPicker(): void {
    if (this.hasUpsTracking || !this.accessPointPickerDialog) {
      return;
    }

    const recipient = this.recipientForm.getRawValue() as UpsRecipientFormValue;
    if (
      !recipient.line1?.trim() ||
      !recipient.city?.trim() ||
      !recipient.postalCode?.trim() ||
      !recipient.country?.trim()
    ) {
      this.toastr.error("cardOrders.shippingAddressErrors.incomplete");
      return;
    }

    this.accessPointPickerControl.setValue(recipient.accessPointId);
    this.accessPointPickerDialogRef = this.dialog.open(
      this.accessPointPickerDialog,
      {
        width: "960px",
        maxWidth: "96vw",
        autoFocus: "first-tabbable",
        panelClass: "app-dialog-panel",
      },
    );
    this.refreshAccessPoints();

    this.accessPointPickerDialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.accessPointPickerDialogRef = null;
      });
  }

  closeAccessPointPicker(): void {
    this.accessPointPickerDialogRef?.close();
  }

  openNfcItemPreview(item: PrintCardNfcEncodingItem): void {
    const orderId = this.order?.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }
    openPrintCardOrderItemPreviewDialog(this.dialog, {
      mode: "order",
      orderId,
      previewApi: "admin",
      itemId: item.itemId,
      hasPdf: item.hasPdf,
      title: `#${item.printerOrderNumber} · ${item.templateName}`,
      subtitle: item.employeeName,
    });
  }

  printNameLabel(employeeEmail?: string | null): void {
    const order = this.order;
    const orderId = order?.printCardOrderId?.trim();
    if (!order || !orderId || this.nameLabelLoading) {
      return;
    }

    const email =
      employeeEmail?.trim().toLowerCase() ||
      order.employeeEmail?.trim().toLowerCase() ||
      "";

    const loadingKey = email || orderId;
    this.nameLabelLoading = loadingKey;
    this.ordersService
      .getNameLabel(orderId, email || null)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.nameLabelLoading === loadingKey) {
            this.nameLabelLoading = null;
          }
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (label) => {
          if (!label.qrDataUrl?.trim()) {
            this.toastr.error("printCardOrders.nameLabel.qrFailed");
            return;
          }
          const hasName =
            Boolean(label.givenName?.trim()) ||
            Boolean(label.familyName?.trim()) ||
            Boolean(label.displayName?.trim());
          if (!hasName) {
            this.toastr.error("printCardOrders.nameLabel.missingName");
            return;
          }
          void printEmployeeNameLabel({
            givenName: label.givenName,
            familyName: label.familyName,
            qrDataUrl: label.qrDataUrl,
          }).catch(() => {
            this.toastr.error("printCardOrders.nameLabel.qrFailed");
          });
        },
        error: () => {
          this.toastr.error("printCardOrders.nameLabel.qrFailed");
        },
      });
  }

  openPackLinePreview(lineItem: PrintCardUpsPackLineItem): void {
    const orderId = this.order?.printCardOrderId?.trim();
    if (!orderId) {
      return;
    }
    const orderItem = this.order?.recipients
      .flatMap((recipient) => recipient.items)
      .find((entry) => entry.id === lineItem.id);
    openPrintCardOrderItemPreviewDialog(this.dialog, {
      mode: "order",
      orderId,
      previewApi: "admin",
      itemId: lineItem.id,
      hasPdf: orderItem?.hasPdf ?? lineItem.hasPdf,
      title: `#${lineItem.printerOrderNumber} · ${lineItem.templateName}`,
      subtitle: lineItem.employeeName?.trim() || lineItem.employeeEmail,
    });
  }

  confirmAccessPointPicker(): void {
    const selectedId = this.accessPointPickerControl.value.trim();
    const point = this.accessPoints.find((item) => item.id === selectedId);
    if (!point) {
      this.toastr.error("cardOrders.shippingAddressErrors.accessPointRequired");
      return;
    }

    this.applyAccessPointSelection(point);
    this.closeAccessPointPicker();
  }

  get canConfirmAccessPointPicker(): boolean {
    return Boolean(this.accessPointPickerControl.value.trim());
  }

  private applyAccessPointSelection(point: CardOrderUpsAccessPoint): void {
    this.recipientForm.patchValue({
      accessPointId: point.id,
      accessPointName: point.name,
      accessPointLine1: point.line1,
      accessPointCity: point.city,
      accessPointPostalCode: point.postalCode,
      accessPointCountry: point.country,
      accessPointOpeningHours: point.openingHours ?? "",
    });
  }

  private refreshAccessPoints(): void {
    const recipient = this.recipientForm.getRawValue() as UpsRecipientFormValue;
    if (
      !recipient.line1?.trim() ||
      !recipient.city?.trim() ||
      !recipient.postalCode?.trim() ||
      !recipient.country?.trim()
    ) {
      this.accessPoints = [];
      return;
    }

    const requestSeq = ++this.accessPointsRequestSeq;
    this.accessPointsLoading = true;
    this.upsService
      .searchAccessPoints({
        line1: recipient.line1,
        city: recipient.city,
        postalCode: recipient.postalCode,
        country: recipient.country,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (points) => {
          if (requestSeq !== this.accessPointsRequestSeq) {
            return;
          }
          this.accessPoints = points;
          this.accessPointsLoading = false;
          const selectedId = this.accessPointPickerControl.value.trim();
          const hasSelection = points.some((point) => point.id === selectedId);
          if (points.length === 1 && !hasSelection) {
            this.accessPointPickerControl.setValue(points[0].id);
          }
          if (!hasSelection && points.length !== 1) {
            this.accessPointPickerControl.setValue("");
          }
        },
        error: (error) => {
          if (requestSeq !== this.accessPointsRequestSeq) {
            return;
          }
          this.accessPoints = [];
          this.accessPointsLoading = false;
          this.toastr.apiError(
            error,
            "cardOrders.shippingAddressErrors.accessPointsSearchFailed",
          );
        },
      });
  }

  private setupRecipientDeliveryFlow(): void {
    this.recipientForm
      .get("deliveryType")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((deliveryType: "COURIER" | "ACCESS_POINT" | "PICKUP") => {
        this.onRecipientDeliveryTypeChanged(deliveryType);
        this.syncIncompatibleAccessorials();
      });

    const shipperCountryControl = this.shipperForm.get("country");
    const recipientFieldChanges = (
      ["line1", "city", "postalCode", "country"] as const
    )
      .map((field) => this.recipientForm.get(field))
      .filter((control): control is AbstractControl => control !== null)
      .map((control) => control.valueChanges);

    merge(
      ...recipientFieldChanges,
      ...(shipperCountryControl ? [shipperCountryControl.valueChanges] : []),
    )
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncUpsShipmentForCountries();
      });

    this.recipientForm
      .get("country")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((country: string) => {
        this.applyNotificationLocaleDefaults(String(country ?? "PL"));
      });
  }

  private setupShipmentQuoteRefresh(): void {
    this.shipmentForm
      .get("shipmentIndicationCode")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncIncompatibleAccessorials();
      });

    this.shipmentForm
      .get("serviceCode")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncIncompatibleAccessorials();
      });

    this.shipmentForm
      .get("weightUnit")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((weightUnit: string) => {
        const nextWeightUnit = weightUnit === "LBS" ? "LBS" : "KGS";
        const dimensionUnit = nextWeightUnit === "LBS" ? "IN" : "CM";
        if (this.shipmentForm.get("dimensionUnit")?.value !== dimensionUnit) {
          this.shipmentForm.patchValue({ dimensionUnit }, { emitEvent: false });
        }
        this.upsPackages = this.upsPackages.map((pkg) => ({
          ...pkg,
          weightUnit: nextWeightUnit,
          dimensionUnit,
        }));
      });

    this.shipmentForm
      .get("dimensionUnit")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((dimensionUnit: string) => {
        const nextDimensionUnit = dimensionUnit === "IN" ? "IN" : "CM";
        const weightUnit = nextDimensionUnit === "IN" ? "LBS" : "KGS";
        if (this.shipmentForm.get("weightUnit")?.value !== weightUnit) {
          this.shipmentForm.patchValue({ weightUnit }, { emitEvent: false });
        }
        this.upsPackages = this.upsPackages.map((pkg) => ({
          ...pkg,
          weightUnit,
          dimensionUnit: nextDimensionUnit,
        }));
      });
  }

  private syncUpsShipmentForCountries(): void {
    if (!this.upsOptions || this.hasUpsTracking) {
      return;
    }

    const domesticDefault = this.upsOptions.defaults.serviceCode;
    const internationalDefault =
      this.upsOptions.defaults.internationalServiceCode;
    const shipFrom = upsCountryCode(
      String(this.shipperForm.get("country")?.value ?? "PL"),
    );
    const shipTo = upsCountryCode(
      String(this.recipientForm.get("country")?.value ?? "PL"),
    );
    const international = shipFrom !== shipTo;
    const current = String(this.shipmentForm.get("serviceCode")?.value ?? "");
    const internationalServiceCodes = new Set(
      this.upsOptions.internationalServiceCodes ?? [],
    );
    const domesticServiceCodes = new Set(
      this.upsOptions.domesticServiceCodes ??
        this.upsOptions.services.map((option) => option.code),
    );
    let next: string;
    if (international) {
      next =
        current && internationalServiceCodes.has(current)
          ? current
          : internationalDefault;
    } else {
      next =
        current && domesticServiceCodes.has(current)
          ? current
          : domesticDefault;
    }

    if (next !== current) {
      this.shipmentForm.patchValue({ serviceCode: next }, { emitEvent: false });
    }

    const customsEnabledControl = this.internationalFormsForm.get("enabled");
    const customsEnabled = Boolean(customsEnabledControl?.value);
    if (international !== customsEnabled) {
      if (international && this.order) {
        this.internationalFormsForm.patchValue(
          {
            enabled: true,
            ...this.buildInternationalFormsDefaults(
              this.order,
              this.upsOptions,
            ),
          },
          { emitEvent: false },
        );
        this.updateInternationalValidators(true);
      } else {
        this.internationalFormsForm.patchValue(
          {
            enabled: false,
            formTypes: [],
            termsOfShipment: "",
            reasonForExport: "",
            comments: "",
            declarationStatement: "",
          },
          { emitEvent: false },
        );
        this.updateInternationalValidators(false);
      }
    }
    if (!international) {
      customsEnabledControl?.disable({ emitEvent: false });
    } else if (!this.upsFormsLocked) {
      customsEnabledControl?.enable({ emitEvent: false });
    }
  }

  private onRecipientDeliveryTypeChanged(
    deliveryType: "COURIER" | "ACCESS_POINT" | "PICKUP",
  ): void {
    if (this.order) {
      this.order = {
        ...this.order,
        deliveryType,
        shipmentCarrier:
          deliveryType === "PICKUP" ? null : this.order.shipmentCarrier,
        upsAccessPoint:
          deliveryType === "ACCESS_POINT" ? this.order.upsAccessPoint : null,
      };
    }
    this.updateRecipientAddressValidators(deliveryType !== "PICKUP");
    this.updateAccessPointValidators(deliveryType === "ACCESS_POINT");
    this.syncIncompatibleAccessorials();
    this.updateNotificationValidators();
    if (deliveryType === "ACCESS_POINT") {
      this.shipmentForm.patchValue(
        { shipmentIndicationCode: "02", deliveryConfirmation: "" },
        { emitEvent: false },
      );
      this.notificationForm.patchValue(
        { qvnEmailEnabled: false },
        { emitEvent: false },
      );
      this.applyAccessPointNotificationDefaults();
      if (!this.hasSelectedAccessPoint) {
        this.openAccessPointPicker();
      }
      return;
    }
    this.shipmentForm.patchValue(
      { shipmentIndicationCode: "" },
      { emitEvent: false },
    );
    this.clearAccessPointSelection();
  }

  private updateRecipientAddressValidators(required: boolean): void {
    const addressFields = [
      "line1",
      "city",
      "postalCode",
      "country",
      "addressLocationType",
    ] as const;
    for (const field of addressFields) {
      const control = this.recipientForm.get(field);
      if (!control) {
        continue;
      }
      if (required) {
        control.setValidators(requiredValidator);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private clearAccessPointSelection(): void {
    this.recipientForm.patchValue(
      {
        accessPointId: "",
        accessPointName: "",
        accessPointLine1: "",
        accessPointCity: "",
        accessPointPostalCode: "",
        accessPointCountry: "",
        accessPointOpeningHours: "",
      },
      { emitEvent: false },
    );
  }

  private setupNotificationLocaleSync(): void {
    for (const prefix of ["adl", "uap"] as const) {
      this.notificationForm
        .get(`${prefix}LocaleDialect`)
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((country: string) => {
          if (!country?.trim()) {
            return;
          }
          const locale = upsNotificationLocale(country);
          this.notificationForm.patchValue(
            { [`${prefix}LocaleLanguage`]: locale.language },
            { emitEvent: false },
          );
        });
    }
  }

  private setupConditionalValidators(): void {
    this.notificationForm
      .get("qvnEmailEnabled")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateNotificationValidators();
      });

    const accessPointNotificationFields = ["adlChannel", "uapChannel"] as const;
    for (const field of accessPointNotificationFields) {
      this.notificationForm
        .get(field)
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateNotificationValidators();
        });
    }

    this.internationalFormsForm
      .get("enabled")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        this.updateInternationalValidators(enabled);
        if (enabled && this.order) {
          this.patchInternationalFormsDefaultsIfEmpty(this.order);
        }
      });

    this.shipmentForm
      .get("pickupEnabled")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        this.syncShipmentPickupValidators(Boolean(enabled));
      });
  }

  private syncShipmentPickupValidators(required: boolean): void {
    for (const field of ["pickupDate", "readyTime", "closeTime"] as const) {
      const control = this.shipmentForm.get(field);
      if (!control) {
        continue;
      }
      if (required) {
        control.setValidators(requiredValidator);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private initializeConditionalValidators(): void {
    const deliveryType = this.recipientForm.get("deliveryType")
      ?.value as UpsRecipientFormValue["deliveryType"];
    this.updateRecipientAddressValidators(deliveryType !== "PICKUP");
    this.updateAccessPointValidators(deliveryType === "ACCESS_POINT");
    this.syncIncompatibleAccessorials();
    this.updateNotificationValidators();
    this.updateInternationalValidators(
      Boolean(this.internationalFormsForm.get("enabled")?.value),
    );
    this.syncShipmentPickupValidators(
      Boolean(this.shipmentForm.get("pickupEnabled")?.value),
    );
  }

  private updateAccessPointValidators(required: boolean): void {
    const accessPointFields = [
      "accessPointId",
      "accessPointName",
      "accessPointLine1",
      "accessPointCity",
      "accessPointPostalCode",
      "accessPointCountry",
    ];

    for (const field of accessPointFields) {
      const control = this.recipientForm.get(field);
      if (!control) {
        continue;
      }
      if (required) {
        control.setValidators(requiredValidator);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    }

    const indication = this.shipmentForm.get("shipmentIndicationCode");
    if (required) {
      indication?.setValidators(requiredValidator);
    } else {
      indication?.clearValidators();
    }
    indication?.updateValueAndValidity({ emitEvent: false });
  }

  private updateNotificationValidators(): void {
    const qvnEmail = this.notificationForm.get("qvnEmail");
    const qvnEmailCode = this.notificationForm.get("qvnEmailNotificationCode");
    const qvnEmailEnabledControl = this.notificationForm.get("qvnEmailEnabled");

    if (this.isAccessPointDelivery) {
      if (qvnEmailEnabledControl?.value) {
        this.notificationForm.patchValue(
          { qvnEmailEnabled: false },
          { emitEvent: false },
        );
      }
      qvnEmail?.clearValidators();
      qvnEmailCode?.clearValidators();
      qvnEmail?.updateValueAndValidity({ emitEvent: false });
      qvnEmailCode?.updateValueAndValidity({ emitEvent: false });
      this.updateAccessPointNotificationValidators("adl", true);
      this.updateAccessPointNotificationValidators("uap", true);
      return;
    }

    const qvnEmailEnabled = Boolean(qvnEmailEnabledControl?.value);
    if (qvnEmailEnabled) {
      qvnEmail?.setValidators([requiredValidator, emailValidator]);
      qvnEmailCode?.setValidators(requiredValidator);
    } else {
      qvnEmail?.clearValidators();
      qvnEmailCode?.clearValidators();
    }
    qvnEmail?.updateValueAndValidity({ emitEvent: false });
    qvnEmailCode?.updateValueAndValidity({ emitEvent: false });

    this.updateAccessPointNotificationValidators("adl", false);
    this.updateAccessPointNotificationValidators("uap", false);
  }

  private updateAccessPointNotificationValidators(
    prefix: "adl" | "uap",
    required: boolean,
  ): void {
    const channelControl = this.notificationForm.get(`${prefix}Channel`);
    const emailControl = this.notificationForm.get(`${prefix}Email`);
    const phoneControl = this.notificationForm.get(`${prefix}Phone`);
    const languageControl = this.notificationForm.get(
      `${prefix}LocaleLanguage`,
    );
    const dialectControl = this.notificationForm.get(`${prefix}LocaleDialect`);

    if (!required) {
      channelControl?.clearValidators();
      emailControl?.clearValidators();
      phoneControl?.clearValidators();
      languageControl?.clearValidators();
      dialectControl?.clearValidators();
      channelControl?.updateValueAndValidity({ emitEvent: false });
      emailControl?.updateValueAndValidity({ emitEvent: false });
      phoneControl?.updateValueAndValidity({ emitEvent: false });
      languageControl?.updateValueAndValidity({ emitEvent: false });
      dialectControl?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    languageControl?.setValidators(requiredValidator);
    dialectControl?.setValidators(requiredValidator);
    channelControl?.setValidators(requiredValidator);

    const channel = channelControl?.value as UpsNotificationChannel;
    if (channel === "email") {
      emailControl?.setValidators([requiredValidator, emailValidator]);
      phoneControl?.clearValidators();
    } else {
      phoneControl?.setValidators(phoneValidators);
      emailControl?.clearValidators();
    }

    channelControl?.updateValueAndValidity({ emitEvent: false });
    emailControl?.updateValueAndValidity({ emitEvent: false });
    phoneControl?.updateValueAndValidity({ emitEvent: false });
    languageControl?.updateValueAndValidity({ emitEvent: false });
    dialectControl?.updateValueAndValidity({ emitEvent: false });
  }

  private updateInternationalValidators(enabled: boolean): void {
    const fields: Array<{
      name: string;
      validators: NonNullable<Parameters<AbstractControl["setValidators"]>[0]>;
    }> = [
      { name: "formTypes", validators: requiredFormTypesValidator },
      { name: "termsOfShipment", validators: requiredValidator },
      { name: "reasonForExport", validators: requiredValidator },
      { name: "productDescription", validators: requiredValidator },
      { name: "productOriginCountry", validators: requiredValidator },
      { name: "currencyCode", validators: requiredValidator },
      { name: "productUnitNumber", validators: requiredValidator },
      { name: "productUnitValue", validators: requiredValidator },
      { name: "productUnitOfMeasure", validators: requiredValidator },
    ];

    for (const { name, validators } of fields) {
      const control = this.internationalFormsForm.get(name);
      if (!control) {
        continue;
      }
      if (enabled) {
        control.setValidators(validators);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private populateRecipientForm(order: PrintCardOrder): void {
    const recipientName =
      order.recipientName?.trim() ||
      order.employeeName?.trim() ||
      order.employeeEmail;
    const shipping = order.shippingAddress;
    this.recipientForm.patchValue(
      {
        recipientName,
        companyName: order.companyName?.trim() || "",
        phone: order.phone ?? "",
        line1: shipping?.line1 ?? "",
        line2: shipping?.line2 ?? "",
        city: shipping?.city ?? "",
        postalCode: shipping?.postalCode ?? "",
        country: (shipping?.country ?? "PL").trim().toUpperCase(),
        deliveryType: order.deliveryType ?? "COURIER",
        accessPointId: order.upsAccessPoint?.id ?? "",
        accessPointName: order.upsAccessPoint?.name ?? "",
        accessPointLine1: order.upsAccessPoint?.line1 ?? "",
        accessPointCity: order.upsAccessPoint?.city ?? "",
        accessPointPostalCode: order.upsAccessPoint?.postalCode ?? "",
        accessPointCountry: (order.upsAccessPoint?.country ?? "")
          .trim()
          .toUpperCase(),
        accessPointOpeningHours: order.upsAccessPoint?.openingHours ?? "",
        addressLocationType: "COMMERCIAL",
      },
      { emitEvent: false },
    );
    const locale = upsNotificationLocale((shipping?.country ?? "PL").trim());
    const notificationEmail = this.getDefaultUpsNotificationEmail();
    this.notificationForm.patchValue({
      qvnEmail: notificationEmail,
      adlEmail: notificationEmail,
      adlPhone: order.phone ?? "",
      uapEmail: notificationEmail,
      uapPhone: order.phone ?? "",
      adlLocaleLanguage: locale.language,
      adlLocaleDialect: locale.dialect,
      uapLocaleLanguage: locale.language,
      uapLocaleDialect: locale.dialect,
    });
  }

  private applyShipmentDefaults(
    order: PrintCardOrder,
    upsOptions: UpsShipmentOptions,
  ): void {
    const defaults = upsOptions.defaults;
    const shipFrom = upsCountryCode(
      String(
        this.shipperForm.get("country")?.value ??
          order.shipper?.country ??
          "PL",
      ),
    );
    const shipTo = upsCountryCode(
      String(
        this.recipientForm.get("country")?.value ??
          order.shippingAddress?.country ??
          "PL",
      ),
    );
    const international = shipFrom !== shipTo;
    const serviceCode = international
      ? defaults.internationalServiceCode
      : defaults.serviceCode;
    const defaultDcis = String(defaults.deliveryConfirmation ?? "").trim();
    this.shipmentForm.patchValue({
      serviceCode,
      packagingCode: defaults.packagingCode,
      ratingRequestOption: defaults.ratingRequestOption,
      weightUnit: defaults.weightUnit,
      dimensionUnit: defaults.dimensionUnit,
      deliveryConfirmation:
        defaultDcis === "2" || defaultDcis === "3" ? defaultDcis : "",
      shipmentDescription: `DBC1 print cards ${order.printCardOrderId?.trim() ?? ""}`,
      shipmentIndicationCode: order.deliveryType === "ACCESS_POINT" ? "02" : "",
    });

    this.internationalFormsForm.patchValue(
      international
        ? this.buildInternationalFormsDefaults(order, upsOptions)
        : {
            enabled: false,
            formTypes: [],
            termsOfShipment: "",
            reasonForExport: "",
            comments: "",
            declarationStatement: "",
          },
      { emitEvent: false },
    );
  }

  private buildInternationalFormsDefaults(
    order: PrintCardOrder,
    upsOptions: UpsShipmentOptions,
  ): Partial<UpsInternationalFormsFormValue> {
    const defaults = upsOptions.defaults;
    const totalQuantity = Math.max(getOrderTotalQuantity(order), 1);

    return {
      enabled: true,
      formTypes: ["01"],
      additionalDocumentIndicator: false,
      invoiceNumber: (order.printCardOrderId?.trim() ?? "").slice(0, 35),
      invoiceDate: new Date().toISOString().slice(0, 10).replace(/-/g, ""),
      purchaseOrderNumber: "",
      termsOfShipment: "DDP",
      reasonForExport: "SALE",
      comments: "Wizytówki firmowe",
      declarationStatement:
        "Niniejszym oświadczam, że powyższe informacje są zgodne z prawdą.",
      currencyCode: defaults.currencyCode,
      productDescription: "Wizytówki",
      productCommodityCode: "49119900",
      productOriginCountry: "PL",
      productUnitNumber: String(totalQuantity),
      productUnitValue: "1",
      productUnitOfMeasure: defaults.productUnitOfMeasure,
    };
  }

  private patchInternationalFormsDefaultsIfEmpty(order: PrintCardOrder): void {
    if (!this.upsOptions) {
      return;
    }

    const defaults = this.buildInternationalFormsDefaults(
      order,
      this.upsOptions,
    );
    const current =
      this.internationalFormsForm.getRawValue() as UpsInternationalFormsFormValue;

    this.internationalFormsForm.patchValue(
      {
        formTypes: current.formTypes.length
          ? current.formTypes
          : (defaults.formTypes ?? []),
        invoiceNumber:
          current.invoiceNumber.trim() || defaults.invoiceNumber || "",
        invoiceDate: current.invoiceDate.trim() || defaults.invoiceDate || "",
        purchaseOrderNumber:
          current.purchaseOrderNumber.trim() ||
          defaults.purchaseOrderNumber ||
          "",
        termsOfShipment:
          current.termsOfShipment.trim() || defaults.termsOfShipment || "",
        reasonForExport:
          current.reasonForExport.trim() || defaults.reasonForExport || "",
        comments: current.comments.trim() || defaults.comments || "",
        declarationStatement:
          current.declarationStatement.trim() ||
          defaults.declarationStatement ||
          "",
        currencyCode:
          current.currencyCode.trim() || defaults.currencyCode || "PLN",
        productDescription:
          current.productDescription.trim() ||
          defaults.productDescription ||
          "",
        productCommodityCode:
          current.productCommodityCode.trim() ||
          defaults.productCommodityCode ||
          "",
        productOriginCountry:
          current.productOriginCountry.trim() ||
          defaults.productOriginCountry ||
          "PL",
        productUnitNumber:
          current.productUnitNumber.trim() || defaults.productUnitNumber || "1",
        productUnitValue:
          current.productUnitValue.trim() || defaults.productUnitValue || "1",
        productUnitOfMeasure:
          current.productUnitOfMeasure.trim() ||
          defaults.productUnitOfMeasure ||
          "PCS",
      },
      { emitEvent: false },
    );
  }

  private applyShipperFromOrderOrDefaults(
    order: PrintCardOrder,
    defaults: PrintCardShipperDefaults | null,
  ): void {
    if (order.shipper) {
      this.shipperForm.patchValue(
        {
          name: order.shipper.name,
          line1: order.shipper.line1,
          line2: order.shipper.line2 ?? "",
          city: order.shipper.city,
          postalCode: order.shipper.postalCode,
          country: (order.shipper.country ?? "PL").trim().toUpperCase(),
          phone: order.shipper.phone,
        },
        { emitEvent: false },
      );
      return;
    }

    this.applyShipperDefaults(defaults);
  }

  private buildShipmentPartiesPayload(): UpdatePrintCardOrderShipmentPayload | null {
    if (!this.order) {
      return null;
    }

    const shipper = this.shipperForm.getRawValue() as UpsShipperFormValue;
    const recipient = this.recipientForm.getRawValue() as UpsRecipientFormValue;
    const deliveryType = recipient.deliveryType;
    const fallbackAddress = this.order.shippingAddress;
    const shippingAddress =
      deliveryType === "PICKUP"
        ? {
            line1: recipient.line1.trim() || fallbackAddress?.line1 || "",
            line2:
              recipient.line2?.trim() || fallbackAddress?.line2 || undefined,
            city: recipient.city.trim() || fallbackAddress?.city || "",
            postalCode:
              recipient.postalCode.trim() || fallbackAddress?.postalCode || "",
            country:
              recipient.country.trim() || fallbackAddress?.country || "PL",
          }
        : {
            line1: recipient.line1.trim(),
            line2: recipient.line2?.trim() || undefined,
            city: recipient.city.trim(),
            postalCode: recipient.postalCode.trim(),
            country: recipient.country.trim(),
          };

    if (
      deliveryType !== "PICKUP" &&
      (!shippingAddress.line1 ||
        !shippingAddress.city ||
        !shippingAddress.postalCode ||
        !shippingAddress.country)
    ) {
      return null;
    }

    const companyName = recipient.companyName.trim();
    const payload: UpdatePrintCardOrderShipmentPayload = {
      shipper: {
        name: shipper.name.trim(),
        line1: shipper.line1.trim(),
        line2: shipper.line2?.trim() || undefined,
        city: shipper.city.trim(),
        postalCode: shipper.postalCode.trim(),
        country: shipper.country.trim(),
        phone: shipper.phone.trim(),
      },
      recipientName: recipient.recipientName.trim(),
      ...(companyName ? { companyName } : {}),
      phone: recipient.phone.trim(),
      shippingAddress,
      deliveryType,
    };

    if (deliveryType === "ACCESS_POINT") {
      payload.upsAccessPoint = {
        id: recipient.accessPointId.trim(),
        name: recipient.accessPointName.trim(),
        line1: recipient.accessPointLine1.trim(),
        city: recipient.accessPointCity.trim(),
        postalCode: recipient.accessPointPostalCode.trim(),
        country:
          recipient.accessPointCountry.trim() || recipient.country.trim(),
        ...(recipient.accessPointOpeningHours.trim()
          ? { openingHours: recipient.accessPointOpeningHours.trim() }
          : {}),
      };
    } else {
      payload.upsAccessPoint = null;
    }

    return payload;
  }

  private applyShipperDefaults(
    defaults: PrintCardShipperDefaults | null,
  ): void {
    if (!defaults) {
      return;
    }
    this.shipperForm.patchValue(
      {
        name: defaults.name,
        line1: defaults.line1,
        line2: defaults.line2 ?? "",
        city: defaults.city,
        postalCode: defaults.postalCode,
        country: (defaults.country ?? "PL").trim().toUpperCase(),
        phone: defaults.phone,
      },
      { emitEvent: false },
    );
  }

  private createAccessorialsForm(
    options: UpsShipmentSelectOption[],
  ): FormGroup {
    const controls: Record<string, boolean> = {};
    for (const item of options) {
      controls[item.code] = false;
    }
    return this.fb.group(controls);
  }

  private createInternationalFormsForm(): FormGroup {
    return this.fb.group({
      enabled: [false],
      formTypes: [[] as string[]],
      additionalDocumentIndicator: [false],
      invoiceNumber: [""],
      invoiceDate: [""],
      purchaseOrderNumber: [""],
      termsOfShipment: [""],
      reasonForExport: [""],
      comments: [""],
      declarationStatement: [""],
      currencyCode: ["PLN"],
      productDescription: ["Wizytówki"],
      productCommodityCode: ["49119900"],
      productOriginCountry: ["PL"],
      productUnitNumber: ["1"],
      productUnitValue: ["1"],
      productUnitOfMeasure: ["PCS"],
    });
  }

  private createNotificationForm(): FormGroup {
    const locale = upsNotificationLocale("PL");
    return this.fb.group({
      qvnEmailEnabled: [true],
      qvnEmailNotificationCode: ["8"],
      qvnEmail: [""],
      adlChannel: ["sms"],
      adlEmail: [""],
      adlPhone: [""],
      adlLocaleLanguage: [locale.language],
      adlLocaleDialect: [locale.dialect],
      uapChannel: ["sms"],
      uapEmail: [""],
      uapPhone: [""],
      uapLocaleLanguage: [locale.language],
      uapLocaleDialect: [locale.dialect],
    });
  }

  private getShipmentAccessorials(
    form: FormGroup,
  ): UpsShipmentAccessorialsPayload | undefined {
    const raw = form.getRawValue() as Record<string, boolean>;
    const indicationCode = String(
      this.shipmentForm.get("shipmentIndicationCode")?.value ?? "",
    ).trim();
    const result: UpsShipmentAccessorialsPayload = {};
    let hasAny = false;

    const allowDirectDelivery =
      !this.isAccessPointDelivery && Boolean(raw["directDeliveryOnly"]);
    const allowDropoff =
      !this.isAccessPointDelivery &&
      this.isFreightUpsServiceSelected() &&
      Boolean(raw["dropoffAtUpsFacility"]);
    const allowAddresseeOnly =
      this.isAccessPointDelivery &&
      indicationCode === "01" &&
      Boolean(raw["deliverToAddresseeOnly"]);

    if (allowDirectDelivery) {
      result.directDeliveryOnly = true;
      hasAny = true;
    }
    if (allowAddresseeOnly) {
      result.deliverToAddresseeOnly = true;
      hasAny = true;
    }
    if (allowDropoff) {
      result.dropoffAtUpsFacility = true;
      hasAny = true;
    }

    return hasAny ? result : undefined;
  }

  private buildShipmentNotifications(
    notification: UpsNotificationFormValue,
  ): UpsShipmentNotificationPayload[] {
    const notifications: UpsShipmentNotificationPayload[] = [];

    if (
      !this.isAccessPointDelivery &&
      notification.qvnEmailEnabled &&
      notification.qvnEmailNotificationCode &&
      notification.qvnEmail.trim()
    ) {
      notifications.push({
        notificationCode: notification.qvnEmailNotificationCode,
        channel: "email",
        email: notification.qvnEmail.trim(),
      });
    }

    if (!this.isAccessPointDelivery) {
      return notifications;
    }

    const uap = this.buildAccessPointNotification("013", notification, "uap");
    if (uap) {
      notifications.push(uap);
    }

    const adl = this.buildAccessPointNotification("012", notification, "adl");
    if (adl) {
      notifications.push(adl);
    }

    return notifications;
  }

  private syncIncompatibleAccessorials(): void {
    if (!this.shipmentAccessorialsForm) {
      return;
    }

    const codes = [
      "directDeliveryOnly",
      "deliverToAddresseeOnly",
      "dropoffAtUpsFacility",
    ] as const;

    for (const code of codes) {
      const control = this.shipmentAccessorialsForm.get(code);
      if (!control) {
        continue;
      }
      const disabled = this.isAccessorialOptionDisabled(code);
      if (disabled) {
        if (control.value) {
          control.setValue(false, { emitEvent: false });
        }
        if (control.enabled) {
          control.disable({ emitEvent: false });
        }
        continue;
      }
      if (control.disabled && !this.upsFormsLocked) {
        control.enable({ emitEvent: false });
      }
    }
  }

  private buildAccessPointNotification(
    notificationCode: "012" | "013",
    notification: UpsNotificationFormValue,
    prefix: "adl" | "uap",
  ): UpsShipmentNotificationPayload | null {
    const channel = notification[`${prefix}Channel`];
    const locale = {
      language: notification[`${prefix}LocaleLanguage`].trim(),
      dialect: upsCountryCode(notification[`${prefix}LocaleDialect`]),
    };

    if (!locale.language || !locale.dialect) {
      return null;
    }

    if (channel === "email") {
      const email = notification[`${prefix}Email`].trim();
      if (!email) {
        return null;
      }
      return {
        notificationCode,
        channel: "email",
        email,
        locale,
      };
    }

    const phone = notification[`${prefix}Phone`].trim();
    if (!phone) {
      return null;
    }

    return {
      notificationCode,
      channel,
      phone,
      locale,
    };
  }

  private applyAccessPointNotificationDefaults(): void {
    const recipient = this.recipientForm.getRawValue() as UpsRecipientFormValue;
    const locale = upsNotificationLocale(recipient.country);
    const qvnEmailRaw: unknown = this.notificationForm.get("qvnEmail")?.value;
    const email =
      (typeof qvnEmailRaw === "string" ? qvnEmailRaw.trim() : "") ||
      this.getDefaultUpsNotificationEmail();
    const phone = recipient.phone ?? "";
    const recipientCountry = upsCountryCode(recipient.country);
    const channel: UpsNotificationChannel =
      recipientCountry === "US" || recipientCountry === "CA" ? "email" : "sms";

    this.notificationForm.patchValue(
      {
        adlChannel: channel,
        uapChannel: channel,
        adlEmail: email,
        adlPhone: phone,
        adlLocaleLanguage: locale.language,
        adlLocaleDialect: locale.dialect,
        uapEmail: email,
        uapPhone: phone,
        uapLocaleLanguage: locale.language,
        uapLocaleDialect: locale.dialect,
      },
      { emitEvent: false },
    );
  }

  private applyNotificationLocaleDefaults(country: string): void {
    const locale = upsNotificationLocale(country);
    this.notificationForm.patchValue(
      {
        adlLocaleLanguage: locale.language,
        adlLocaleDialect: locale.dialect,
        uapLocaleLanguage: locale.language,
        uapLocaleDialect: locale.dialect,
      },
      { emitEvent: false },
    );
  }

  private getDefaultUpsNotificationEmail(): string {
    return (
      this.upsOptions?.defaults.notificationEmail?.trim() || "ups@dbc1.com"
    );
  }
}
